/**
 * RemoteOpsService — cliente SSE+REST da Visualização Interativa.
 *
 * <p>Substituiu a versão WebSocket. Usa o mesmo transport stack do resto
 * do Efflux: {@code EventSource} pra receber comandos do server,
 * {@code fetch}/REST POST pra enviar snapshot e {@code cmdResult}.</p>
 *
 * <p>Endpoints (vide {@code RemoteOpsSseController} + {@code RemoteOpsPlayerController}
 * no backend):</p>
 * <ul>
 *   <li>{@code GET /api/sse/remote/device/{deviceId}?token=<deviceToken>} —
 *       SSE stream pra receber comandos.</li>
 *   <li>{@code POST /api/v1/remote-player/devices/{id}/snapshot/event} — envia frame.</li>
 *   <li>{@code POST /api/v1/remote-player/devices/{id}/commands/{cmdId}/result} —
 *       envia resultado de comando.</li>
 * </ul>
 *
 * <p>Reconnect: o {@code EventSource} reconecta automaticamente em caso de
 * erro de rede; em cima disso, agendamos uma re-instalação com exponential
 * backoff (1s → 30s) se o erro for permanente (auth, 4xx).</p>
 */

import { logSeMudou } from '~/utils/logSeMudou';
import { reconciliarGatilho } from '~/stores/reconciliarGatilho';
import type { TriggerFiredPayload } from '~/services/sync/types';
import { captureScreenBase64 } from '~/utils';
import { detectEngineVersion } from '~/utils/engineVersion';
import { getUptime } from '~/utils/uptime';
import analyticsDB from '~/services/analytics/AnalyticsDB';
import { lastFrameCache } from '~/utils/lastFrameCache';
import { lerDecodersDeVideoEmUso } from '~/services/playback/useSegundoDecoder';
import type { Platform } from '~/services/platform/types';
import { detectPlatform } from '~/services/platform/utils/detectPlatform';
import { createMediaCache } from '~/services/media/cacheFactory';
import { coletarCacheDeMidia } from '~/services/health/mediaCacheStats';
import { coletarMemoriaDoSistema } from '~/services/health/systemMemory';
import { registrarSseZumbi } from '~/services/health/sseTelemetry';
import {
  syncStore,
  mediaStore,
  otaStore,
  streamStore,
  schedulerStore,
} from '~/stores';
import {
  resolvePlaybackContext,
  isVideoOnScreen,
  resolveVideoContentId,
  type PlaybackContext,
} from '~/utils/captureLogicalFallback';
import { logRingBuffer } from '~/services/logging/LogRingBuffer';
import {
  getRecentCrashes,
  markExpectedRestart,
} from '~/services/logging/crashCapture';
import {
  tryClearDeviceOwner,
  getNativeKioskBackend,
  listWifiNetworks,
  connectWifiNetwork,
} from '~/services/platform/kioskMode';
import {
  setTizenUSBLock,
  setTizenIRRemoteLock,
  setTizenScheduledPower,
  rebootTizenDevice,
  installTizenPackage,
  getTizenKioskCapabilities,
} from '~/services/platform/tizenKiosk';
import { kioskStore } from '~/stores/kiosk.store';
import {
  isWaitingDeviceApproval,
  triggerAuthRecovery,
} from '~/utils/deviceApprovalState';
import { otaLog } from '~/services/ota/OtaDebugLogger';
import { unifiedContentChannel } from '~/services/sse/unifiedContentChannel';
import {
  parseContentEnvelope,
  isForwardedContentEventType,
} from '~/services/sse/contentEventDispatch';

interface RemoteOpsConfig {
  /** Base URL HTTP do server. */
  apiUrl: string;
  /** UUID ou external_id usado no path do SSE/REST. */
  deviceId: string;
  /**
   * Hardware ID (ANDROID-xxx) para headers X-Device-Id em /sse-token.
   * Deve bater com o sync regular; quando omitido, usa {@link deviceId}.
   */
  authDeviceId?: string;
  /** Token UUID do device (query param ?token= no SSE, X-Device-Token nos POSTs). */
  deviceToken: string;
}

interface IncomingCmd {
  type?: string;
  cmdId: string;
  command: string;
  params?: Record<string, unknown>;
}

import {
  executeSafePageReload,
  getSafeReloadDelayMs,
} from '~/utils/safePageReload';
import { LAST_RELOAD_AT_KEY } from '~/services/storageKeys';
import { msAteRenovarToken } from './renovacaoDeToken';

const LOG = '[RemoteOps]';
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30_000;
/** Teto pra segurar a conexão antiga durante uma renovação (make-before-break). */
const SSE_MAKE_BEFORE_BREAK_TETO_MS = 10_000;
/**
 * Renovar o token POR RELÓGIO, mesmo com o canal saudável.
 *
 * <p><b>Desligado — medido em produção em 2026-10-16.</b> Numa janela de 22,6 min com
 * 5 players: 47 subscribes, dos quais <b>41 foram esta renovação e só 1 foi queda real
 * de stream</b> (4 dos 5 players: zero quedas). Ou seja ~24 reconexões forçadas por
 * player/hora contra ~0,5 queda real — <b>98% das reconexões do sistema eram
 * auto-infligidas</b>. A 6000 players isso é 40 req/s em {@code POST
 * /api/devices/sse-token} e ~100 consultas/s no banco, para proteger de algo que
 * acontece menos de uma vez por segundo na frota inteira.</p>
 *
 * <p><b>O token não sustenta o stream.</b> O servidor o confere UMA vez, no connect:
 * é {@code SseEmitter(Long.MAX_VALUE)}, sem evicção por vencimento. Medido: um stream
 * de produção seguiu vivo com heartbeats <b>90s depois do token vencer</b>, e só
 * terminou porque o cliente desistiu. Renovar o token não prolonga nada — só reconecta.</p>
 *
 * <p><b>O que perdemos:</b> numa queda real com token já vencido, o EventSource tenta
 * uma vez com a URL velha e toma 401. Isso NÃO entra em laço — pela especificação ele
 * vai pra CLOSED, e o nosso {@code onerror} assume com token fresco (~1s). Um 401 por
 * queda real, contra 24 reconexões por hora que deixam de existir.</p>
 *
 * <p>Religar é trocar isto pra {@code true} — o caminho de renovação segue inteiro e
 * testado logo abaixo.</p>
 */
const RENOVACAO_DE_TOKEN_POR_RELOGIO = false;
/** Erros CONNECTING consecutivos → reconecta com token novo (browser reusa URL stale). */
const CONNECTING_ERROR_THRESHOLD = 4;
const CONNECTING_ERROR_WINDOW_MS = 20_000;
/**
 * Watchdog de silêncio (mata socket "zumbi" do WebKit Tizen — OPEN sem `onerror`).
 * O server manda um evento `ping` observável a cada ~25s; se passar deste tempo
 * SEM nenhum evento (hello/ping/cmd) com a conexão OPEN, tratamos como morta e
 * forçamos reconnect. Sem isto, o EventSource fica OPEN pra sempre e o RemoteOps
 * some (device offline / comandos em timeout) enquanto sync/heartbeat seguem OK.
 */
const SSE_SILENCE_THRESHOLD_MS = 70_000;
const SSE_WATCHDOG_INTERVAL_MS = 20_000;
/**
 * Timeout (ms) dos fetches HTTP do serviço (SSE token, cmd-result, snapshot). SEM isto o
 * fetch PENDURA PRA SEMPRE numa WebKit Tizen degradando (fetch pendurado NUNCA rejeita, então
 * o `.catch` não pega). No `connect()`, isso deixa es=null indefinidamente e o watchdog fica
 * CEGO (`!es → return`) → canal morto até o reboot. Foi a causa do 409 de ~2h (SSSP 2026-07-25):
 * app vivo mas sufocado, SSE de RemoteOps morto sem nada que o ressuscitasse.
 */
const SSE_FETCH_TIMEOUT_MS = 10_000;
/**
 * Se `connect()` ficou com es=null por mais que isto SEM reconnect agendado, assumimos que
 * encalhou (ex.: um await que não retornou) e o watchdog força uma nova conexão — rede de
 * segurança independente do estado OPEN-zumbi. Maior que {@link SSE_FETCH_TIMEOUT_MS} pra dar
 * folga a um connect() legítimo (que fecha em ≤10s com o timeout do fetch acima) terminar.
 */
const SSE_STUCK_CONNECT_MS = 30_000;
/** Reconecta ao voltar ao foreground só se o canal ficou parado mais que isto. */
const VISIBILITY_STALE_MS = 10_000;
/** Tizen: html2canvas bloqueia main thread — min interval entre captures. */
const TIZEN_SNAPSHOT_MIN_INTERVAL_MS = 12_000;
/** Android: 1 capture/s (requisito Visualização Interativa burst). */
const ANDROID_SNAPSHOT_MIN_INTERVAL_MS = 1_000;
/**
 * `app:restart`: quanto o player espera antes de matar o próprio processo.
 *
 * O resultado do comando sai num POST que ninguém aguarda — ele começa logo
 * depois do handler retornar. Esta janela é o tempo que esse POST tem pra
 * deixar o aparelho antes do processo morrer. 1,5 s é folgado pra rede de
 * loja e irrelevante diante dos ~6-10 s de tela preta do próprio restart.
 */
const RESTART_GRACE_MS = 1_500;

interface HandlerResult {
  status: 'success' | 'failed' | 'unsupported';
  result?: unknown;
  error?: string;
}

type Handler = (
  cmdId: string,
  params: Record<string, unknown>,
) => Promise<HandlerResult>;

class RemoteOpsService {
  private config: RemoteOpsConfig | null = null;
  private es: EventSource | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  /** Erros CONNECTING consecutivos (browser retry com URL/token stale) na janela. */
  /**
   * Reconciliar o gatilho no próximo `hello`. Marcado a cada `connect()` para valer UMA vez
   * por conexão: o `hello` chega com frequência e um GET por `hello` seria desperdício.
   */
  private reconciliarGatilhoNoProximoHello = true;
  private connectingErrorCount = 0;
  private connectingErrorWindowStart = 0;
  private explicitlyStopped = false;
  private lastSnapshotAt = 0;
  /** Evita app:reload durante html2canvas (crash WebView Tab A7). */
  private snapshotCaptureInFlight: Promise<HandlerResult> | null = null;
  /** Timer da renovação PROATIVA do token SSE (reconecta com token fresco antes de vencer). */
  private renewalTimer: number | null = null;
  /**
   * Instante ABSOLUTO (ms) em que o SSE token atual vence — base da renovação proativa.
   *
   * <p>Guardamos o vencimento, e não o TTL, de propósito: o `hello` que dispara o
   * agendamento chega a cada subscribe (inclusive nas reconexões nativas do
   * `EventSource`, que reusam o MESMO token), e contar o TTL do zero a cada hello
   * empurrava a renovação pra depois da morte do token. Vide {@link msAteRenovarToken}.</p>
   */
  private sseTokenExpiraEmMs: number | null = null;
  /** true = conectado com JWT SSE (renova); false = fallback deviceToken legacy (não expira). */
  private usingSseToken = false;
  /** Timestamp do último evento SSE recebido (hello/ping/cmd) — base do watchdog de silêncio. */
  private lastActivityAt = 0;
  /** Quando o `connect()` atual começou (ms). O watchdog usa pra detectar connect() encalhado
   *  (es=null há tempo demais) e forçar recovery — vide {@link SSE_STUCK_CONNECT_MS}. */
  private connectStartedAt = 0;
  /** Interval do watchdog de silêncio (detecta socket zumbi OPEN sem onerror). */
  private watchdogTimer: number | null = null;
  /** Listeners de rede/visibilidade (reconexão proativa) — guardados pra remover no stop(). */
  private boundOnline: (() => void) | null = null;
  private boundVisibility: (() => void) | null = null;
  /**
   * Contagem de reconexões forçadas POR CAUSA desde o boot do player (não zera
   * no stop/start — só num reload, que já é um boot novo). Existe porque não
   * havia NENHUMA telemetria de canal zumbi: dava pra dizer que o watchdog
   * existe, mas não com que frequência ele dispara nem que fração da frota
   * passa por isso. Vide {@link forceReconnect}.
   *
   * As causas NÃO são equivalentes — só as duas primeiras são zumbi de verdade:
   * - `watchdog-silence` — conexão OPEN sem nenhum evento há
   *   {@link SSE_SILENCE_THRESHOLD_MS}. O zumbi clássico do WebKit Tizen.
   * - `watchdog-dead-channel` — `es=null` encalhado sem reconnect agendado.
   * - `network-online` / `visibility-visible` — reconexão PROATIVA, esperada.
   *   Contar junto inflaria a taxa de zumbi.
   * - `connecting-errors` — browser reciclando URL/token stale. Este NÃO passa
   *   pelo `forceReconnect` (fecha + `scheduleReconnect` direto), por isso é
   *   contado no próprio ponto.
   */
  private readonly reconnectCounts = new Map<string, number>();
  /** Timestamp da última reconexão, por causa (ms epoch). */
  private readonly reconnectLastAt = new Map<string, number>();

  /** Map de command name → handler async. */
  private readonly handlers = new Map<string, Handler>([
    ['snapshot:capture', this.handleSnapshotCapture.bind(this)],
    ['sync:force', this.handleSyncForce.bind(this)],
    ['app:reload', this.handleAppReload.bind(this)],
    ['cache:clear', this.handleCacheClear.bind(this)],
    ['health:report', this.handleHealthReport.bind(this)],
    ['logs:tail', this.handleLogsTail.bind(this)],
    ['app:restart', this.handleAppRestart.bind(this)],
    ['device:reboot', this.handleDeviceReboot.bind(this)],
    ['kiosk:clear-device-owner', this.handleKioskClearDeviceOwner.bind(this)],
    ['kiosk:get-state', this.handleKioskGetState.bind(this)],
    // v1.0.110 (Onda 1.4 Sprint K-Pre-Cert) — RemoteOps Tizen SSSP B2B.
    // Degradação graciosa: em Consumer/sem-cert retorna unsupported.
    ['kiosk:set-usb-lock', this.handleKioskSetUSBLock.bind(this)],
    ['kiosk:set-ir-remote-lock', this.handleKioskSetIRRemoteLock.bind(this)],
    ['kiosk:schedule-power', this.handleKioskSchedulePower.bind(this)],
    ['kiosk:reboot-tv', this.handleKioskRebootTV.bind(this)],
    ['ota:install-tizen', this.handleOtaInstallTizen.bind(this)],
    ['kiosk:get-capabilities', this.handleKioskGetCapabilities.bind(this)],
    ['ota:check', this.handleOtaCheck.bind(this)],
    // WIFI_LIST_PLAN — lista rede conectada + proximas (scan). Android-only.
    ['wifi:list', this.handleWifiList.bind(this)],
    // WIFI_CONNECT_PLAN — conecta a uma rede escolhida no admin. Android-only.
    ['wifi:connect', this.handleWifiConnect.bind(this)],
  ]);

  configure(config: RemoteOpsConfig): void {
    this.config = config;
  }

  /**
   * Inicia o EventSource. Idempotente — chama várias vezes não duplica.
   */
  start(): void {
    if (!this.config) {
      console.warn(`${LOG} start() chamado sem configure() prévio`);
      return;
    }
    if (this.es && this.es.readyState !== EventSource.CLOSED) {
      return;
    }
    this.explicitlyStopped = false;
    this.installEnvListeners();
    this.startWatchdog();
    // connect() agora é async (fetchSseToken). Fire-and-forget com .catch
    // pra não deixar Promise rejection sem handler.
    this.connect().catch((err) => {
      otaLog.error('RemoteOps SSE: connect() lançou', { error: String(err) });
      console.error(`${LOG} connect() lançou:`, err);
    });
  }

  /** Fecha o EventSource e cancela reconnects. */
  stop(): void {
    this.explicitlyStopped = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.renewalTimer !== null) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }
    if (this.watchdogTimer !== null) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
    this.removeEnvListeners();
    if (this.es) {
      this.es.close();
      this.es = null;
    }
  }

  /**
   * Força uma reconexão IMEDIATA (fecha a ES atual e reabre com token fresco).
   * Usado pelo watchdog de silêncio e pelos gatilhos de rede/visibilidade —
   * mata o socket "zumbi" (OPEN mas sem stream) que o EventSource não detecta.
   */
  private forceReconnect(reason: string): void {
    if (this.explicitlyStopped || !this.config) return;
    this.contaReconnect(reason);
    otaLog.warn('RemoteOps SSE: forçando reconnect', { reason });
    console.warn(`${LOG} 🔁 forçando reconnect (${reason})`);
    this.reconnectAttempts = 0; // reconnect imediato (sem backoff acumulado)
    // connect() já cancela timers pendentes + fecha a ES anterior no início.
    this.connect().catch((err) => {
      otaLog.error('RemoteOps SSE: connect() (forceReconnect) lançou', {
        error: String(err),
      });
    });
  }

  /**
   * Registra uma reconexão do canal por causa. Só telemetria — nunca lança e
   * nunca muda o fluxo de reconexão. Vide {@link reconnectCounts}.
   */
  private contaReconnect(reason: string): void {
    this.reconnectCounts.set(
      reason,
      (this.reconnectCounts.get(reason) ?? 0) + 1,
    );
    this.reconnectLastAt.set(reason, Date.now());
    // Espelha o total de ZUMBIS num módulo sem dependências, pra série de saúde
    // (montada pelo SyncService) poder ler sem fechar ciclo de import. Vide
    // `services/health/sseTelemetry`.
    if (reason === 'watchdog-silence' || reason === 'watchdog-dead-channel') {
      registrarSseZumbi();
    }
  }

  /**
   * Watchdog: se a conexão está OPEN mas não chega NENHUM evento (hello/ping/cmd)
   * há {@link SSE_SILENCE_THRESHOLD_MS}, o canal é um "zumbi" (típico do WebKit
   * Tizen após sleep/perda de foco/proxy que dropou o stream sem RST) → reconecta.
   * O `onerror` do EventSource não dispara nesse caso, então este é o único detector.
   */
  private startWatchdog(): void {
    if (this.watchdogTimer !== null) return;
    this.watchdogTimer = setInterval(() => {
      if (this.explicitlyStopped) return;
      // 2026-07-25: canal MORTO — es=null há tempo demais SEM reconnect agendado. Sinal de
      // `connect()` ENCALHADO (ex.: fetch do token que pendurou, ou await que não retornou) —
      // o cenário que deixou a SSSP 409 por ~2h com o app vivo. ANTES o watchdog fazia
      // `!es → return`, ficando CEGO exatamente aqui. Agora é a rede de segurança independente:
      // sem conexão viva NEM reconnect pendente há > SSE_STUCK_CONNECT_MS → força uma nova.
      if (!this.es) {
        const downMs =
          Date.now() - Math.max(this.lastActivityAt, this.connectStartedAt);
        if (this.reconnectTimer === null && downMs > SSE_STUCK_CONNECT_MS) {
          otaLog.warn(
            'RemoteOps SSE: watchdog — canal MORTO (es=null) sem reconnect agendado, forçando',
            { downMs },
          );
          console.warn(
            `${LOG} 🐕 watchdog: canal morto ${downMs}ms — forçando reconnect`,
          );
          this.forceReconnect('watchdog-dead-channel');
        }
        return;
      }
      // CLOSED/CONNECTING já são cobertos pelo onerror + scheduleReconnect.
      if (this.es.readyState !== EventSource.OPEN) return;
      const silentMs = Date.now() - this.lastActivityAt;
      if (silentMs > SSE_SILENCE_THRESHOLD_MS) {
        otaLog.warn(
          'RemoteOps SSE: watchdog — canal SILENCIOSO (zumbi), reconectando',
          {
            silentMs,
          },
        );
        console.warn(
          `${LOG} 🐕 watchdog: ${silentMs}ms sem evento — reconectando`,
        );
        this.forceReconnect('watchdog-silence');
      }
    }, SSE_WATCHDOG_INTERVAL_MS) as unknown as number;
  }

  /**
   * Gatilhos de reconexão proativa: (a) navegador voltou pra ONLINE, (b) app
   * voltou ao foreground (TV saiu do sleep / app saiu do background). Ambos são
   * momentos em que um socket zumbi é comum — revalida o canal na hora.
   */
  private installEnvListeners(): void {
    if (this.boundOnline || this.boundVisibility) return; // idempotente
    this.boundOnline = () => {
      otaLog.info('RemoteOps SSE: navegador ONLINE — revalidando canal');
      this.forceReconnect('network-online');
    };
    this.boundVisibility = () => {
      if (
        typeof document === 'undefined' ||
        document.visibilityState !== 'visible'
      ) {
        return;
      }
      if (Date.now() - this.lastActivityAt > VISIBILITY_STALE_MS) {
        otaLog.info(
          'RemoteOps SSE: app VISÍVEL após ausência — revalidando canal',
        );
        this.forceReconnect('visibility-visible');
      }
    };
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('online', this.boundOnline);
    }
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', this.boundVisibility);
    }
  }

  private removeEnvListeners(): void {
    if (this.boundOnline && typeof window !== 'undefined') {
      window.removeEventListener('online', this.boundOnline);
    }
    if (this.boundVisibility && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.boundVisibility);
    }
    this.boundOnline = null;
    this.boundVisibility = null;
  }

  /**
   * Pergunta ao servidor qual gatilho está em curso e se ajusta.
   *
   * <p>O SSE é dispare-e-esqueça: se o canal cair no instante do publish, `trigger_fired` ou
   * `trigger_deactivated` se perdem e NÃO são reenviados. Medido no player web do Renato em
   * 2026-10-15: o token expirou (401), a conexão caiu, e a desativação publicada nessa janela
   * nunca chegou — como CONTINUOUS não tem prazo, o gatilho ficou em loop por mais de uma
   * hora. Ouvir melhor não resolve; perguntar resolve.</p>
   *
   * <p>Best-effort: falha aqui não pode atrapalhar o canal nem o playback. Sem reconciliação
   * o player apenas volta ao comportamento anterior.</p>
   */
  private async reconciliarGatilhoComServidor(): Promise<void> {
    try {
      const { request } = await import('~/api/request');
      const { triggerStore } = await import('~/stores/trigger.store');
      const { data } = await request.get<{
        active: TriggerFiredPayload | null;
      }>('/api/devices/active-trigger');

      const noServidor = data?.active?.triggerId ?? null;
      const noPlayer = triggerStore.activeTrigger?.triggerId ?? null;
      const acao = reconciliarGatilho({
        gatilhoNoServidor: noServidor,
        gatilhoNoPlayer: noPlayer,
      });
      if (acao === 'nada') return;

      otaLog.info('RemoteOps: reconciliando gatilho após (re)conexão', {
        noServidor,
        noPlayer,
        acao,
      });
      if (acao === 'desativar') {
        triggerStore.handleTriggerDeactivated({
          triggerId: noPlayer as number,
          reason: 'reconciliacao',
        });
      } else if (data?.active) {
        triggerStore.handleTriggerFired(data.active);
      }
    } catch (err) {
      // WARN, não debug: esta é a ÚNICA rede que pega um evento perdido na reconexão.
      // Quando ela falha em silêncio, o sintoma aparece longe daqui — gatilho que não
      // entra, ou que não sai — e vira caça ao fantasma. Custo: 1 linha por reconexão
      // com falha, que é raro por definição.
      otaLog.warn('RemoteOps: reconciliação de gatilho FALHOU', {
        error: String(err),
      });
      console.warn(`${LOG} ⚠️ reconciliação de gatilho falhou`, err);
    }
  }

  /**
   * @param manterAnteriorAteOHello renovação PROATIVA: segura a conexão atual no ar até
   *   a nova dizer `hello` (make-before-break). Sem isto, a própria renovação abre um vão
   *   de centenas de ms — e evento SSE que cai no vão NÃO volta. Medido em produção em
   *   2026-10-15: a desativação de um gatilho CONTINUOUS se perdeu exatamente na renovação,
   *   e o conteúdo do gatilho ficou no ar. Seguro porque o servidor já faz last-wins
   *   (`subscribePlayer` substitui e completa o emitter anterior sozinho) — não cria
   *   "emitter zumbi" (task #78).
   */
  private async connect(manterAnteriorAteOHello = false): Promise<void> {
    // Toda (re)conexão abre uma janela em que eventos podem ter se perdido — vide
    // `reconciliarGatilhoComServidor`.
    this.reconciliarGatilhoNoProximoHello = true;
    if (!this.config) return;
    // Marca o início desta tentativa — o watchdog usa pra detectar connect() encalhado (es=null
    // por > SSE_STUCK_CONNECT_MS). Com o timeout do fetch abaixo, um connect() legítimo fecha em
    // ≤10s, então o watchdog (30s) só dispara em encalhe de verdade.
    this.connectStartedAt = Date.now();

    // Cancela timers pendentes e FECHA a conexão anterior antes de abrir a nova.
    // Garante UMA única EventSource viva por vez — abrir uma 2ª sem fechar a 1ª
    // arriscaria 2 emitters no broker pro mesmo device (bug "emitter zumbi",
    // task #78). Por isso a renovação proativa é SEQUENCIAL (fecha→abre).
    if (this.renewalTimer !== null) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    // Numa renovação a anterior fica NO AR até a nova dizer `hello` (ver o parâmetro).
    // Em qualquer outro caminho (reconnect após queda) ela já está morta — fecha logo.
    let anteriorParaFechar: EventSource | null = null;
    if (this.es) {
      if (manterAnteriorAteOHello) {
        anteriorParaFechar = this.es;
      } else {
        this.es.close();
      }
      this.es = null;
    }
    /** Rede de segurança: se a nova nunca disser `hello`, não deixa duas abertas pra sempre. */
    const fecharAnterior = (): void => {
      if (!anteriorParaFechar) return;
      try {
        anteriorParaFechar.close();
      } catch {
        // best-effort — fechar uma ES já encerrada não pode derrubar o canal novo.
      }
      anteriorParaFechar = null;
    };
    if (anteriorParaFechar) {
      window.setTimeout(fecharAnterior, SSE_MAKE_BEFORE_BREAK_TETO_MS);
    }

    // Pentest 2026-06-02 H4 player: o SSE precisa de auth via `?token=` na
    // URL (EventSource API não suporta headers custom). Antes (legacy) o
    // player passava o deviceToken UUID diretamente — credencial de LONGA
    // duração que vazava em logs de proxy/CDN/refer-er.
    //
    // Agora: pega um SSE token short-lived (60s) via POST /api/devices/sse-token
    // e usa esse JWT na URL. Se o fetch falhar (rede ruim, backend antigo
    // sem o endpoint), faz fallback para o deviceToken antigo — garante
    // que rollout backend/player não quebra durante transição.
    const url = await this.buildSseUrl();
    // Loga o path SEM o ?token= (credencial não vaza no console/remote logger).
    console.log(
      `${LOG} 🔌 conectando (unificado): ${url.split('?')[0]}`,
    );

    otaLog.info('RemoteOps SSE: tentando conectar', { url });

    try {
      this.es = new EventSource(url);
      // Conexão nova = atividade agora (evita o watchdog marcar "silêncio" na largada).
      this.lastActivityAt = Date.now();
    } catch (err) {
      otaLog.error('RemoteOps SSE: new EventSource() falhou', {
        error: String(err),
      });
      console.error(`${LOG} new EventSource() falhou:`, err);
      this.scheduleReconnect();
      return;
    }

    // Heartbeat OBSERVÁVEL do server (evento `ping` a cada ~25s). Diferente do
    // comentário `:hb` — este a EventSource entrega ao JS, alimentando o watchdog.
    this.es.addEventListener('ping', () => {
      this.lastActivityAt = Date.now();
      this.connectingErrorCount = 0;
    });

    this.es.addEventListener('hello', (evt: MessageEvent) => {
      this.lastActivityAt = Date.now();
      // A nova está no ar: agora sim a antiga pode sair. Entre o `new EventSource` e este
      // ponto as DUAS escutam, então não há instante sem ninguém ouvindo.
      fecharAnterior();
      try {
        const data = JSON.parse(evt.data);
        // SSE keepalive ~10-15s — debug pra não poluir console
        console.debug(`${LOG} ✅ hello recebido`, data);
        // RUÍDO (2026-10-15): o `hello` é BATIMENTO do canal, não evento — chegava a cada
        // poucos segundos. O que importa é a RECONEXÃO (o canal caiu e voltou), e essa
        // continua logada no caminho de erro/reconnect logo abaixo.
        logSeMudou(
          'remoteops.hello',
          `RemoteOps SSE: canal vivo | deviceId=${data?.deviceId ?? '?'}`,
          (t) => otaLog.info(t),
        );
        if (this.reconciliarGatilhoNoProximoHello) {
          this.reconciliarGatilhoNoProximoHello = false;
          void this.reconciliarGatilhoComServidor();
        }
        // O `deviceId` do hello é o UUID real (tab_players.id) que o servidor
        // resolveu. No EMBEDDED o registro nunca preenche `authStore.playerId`,
        // e sem ele o sync do store é pulado e o config não é ackeado — então
        // aproveitamos este, que já chega aqui. No-op onde já há playerId
        // (frota). Vide AuthStore.adotarPlayerIdResolvido.
        //
        // Import DINÂMICO de propósito: `~/stores` puxa o grafo inteiro de
        // stores e este serviço é carregado cedo — o estático fecharia um ciclo
        // (mesmo motivo do `api/request.ts`). Falha de import não pode derrubar
        // o handler do hello, que mantém o canal vivo.
        const uuidDoHello = data?.deviceId;
        if (uuidDoHello) {
          import('~/stores')
            .then(({ authStore }) => {
              authStore.adotarPlayerIdResolvido(uuidDoHello);
            })
            .catch((err) => {
              console.warn(
                `${LOG} falha ao adotar playerId do hello (segue com deviceId):`,
                err,
              );
            });
        }
      } catch {
        // ignore
      }
      this.reconnectAttempts = 0;
      // Canal vivo de novo → zera o contador de erros CONNECTING.
      this.connectingErrorCount = 0;
      // Agenda renovação PROATIVA do token: reconecta com token fresco ANTES
      // de o atual vencer → o token na URL está sempre válido → mata o 401.
      this.scheduleTokenRenewal();
    });

    this.es.addEventListener('cmd', (evt: MessageEvent) => {
      this.lastActivityAt = Date.now();
      otaLog.info('RemoteOps SSE: event "cmd" recebido', {
        rawDataLength: typeof evt.data === 'string' ? evt.data.length : 0,
        rawDataSample:
          typeof evt.data === 'string'
            ? evt.data.substring(0, 200)
            : '(non-string)',
      });
      // P5 (deep-review #15): handleCmdEvent é async — fire-and-forget sem
      // .catch() vira unhandled-rejection se algum await interno rejeitar
      // por bug ou edge case (ex: handler novo lança fora do try). Catch
      // explícito mantém o handler do EventSource limpo.
      this.handleCmdEvent(evt.data).catch((err) => {
        otaLog.error('RemoteOps SSE: handleCmdEvent uncaught', {
          error: String(err),
        });
        console.error(`${LOG} handleCmdEvent uncaught:`, err);
      });
    });

    // Epic D (Fase D2) — evento nomeado `content` do canal UNIFICADO. Carrega o
    // MESMO envelope do Content SSE legado ({"type","data"}). O EventSource do
    // RemoteOps é hoje a única conexão SSE, então o conteúdo sempre chega por
    // aqui. Disjunto do `cmd` (nomes diferentes) — não interfere no canal de
    // comando.
    this.es.addEventListener('content', (evt: MessageEvent) => {
      this.lastActivityAt = Date.now();
      this.handleContentEvent(evt.data);
    });

    this.es.onerror = () => {
      // EventSource já tem auto-reconnect interno, mas se o erro for
      // permanente (auth, 4xx) ele fecha. Nesse caso, agendamos retry manual.
      if (this.es && this.es.readyState === EventSource.CLOSED) {
        otaLog.warn('RemoteOps SSE: conexão FECHADA — agendando reconnect', {
          attempts: this.reconnectAttempts,
        });
        console.warn(`${LOG} 🔴 SSE fechado — agendando reconnect`);
        this.es = null;
        if (!this.explicitlyStopped) {
          this.scheduleReconnect();
        }
      } else {
        // readyState=CONNECTING (1): o browser está fazendo auto-reconnect
        // NATIVO reusando a MESMA URL — com o JWT SSE possivelmente JÁ EXPIRADO
        // (TTL 60s). Isso gera 401 em loop e nunca chega no 'hello'. Normalmente
        // o heartbeat do server (comment a cada ~25s) mantém a conexão viva e
        // isso quase nunca dispara; mas se o proxy derrubar o stream (max-life,
        // blip de rede), contamos erros CONNECTING consecutivos numa janela e,
        // passando o threshold, FORÇAMOS close + reconnect NOSSO (que busca um
        // token novo em vez de deixar o browser reusar a URL stale → mata o 401).
        const now = Date.now();
        if (
          now - this.connectingErrorWindowStart >
          CONNECTING_ERROR_WINDOW_MS
        ) {
          this.connectingErrorWindowStart = now;
          this.connectingErrorCount = 0;
        }
        this.connectingErrorCount += 1;

        // Renato 2026-06-11: debug (não warn) — CONNECTING é comportamento
        // NORMAL de SSE com keepalive curto. Mantém no LogRingBuffer sem poluir.
        otaLog.debug(
          'RemoteOps SSE: onerror (readyState=CONNECTING — browser retry)',
          {
            readyState: this.es?.readyState ?? -1,
            connectingErrors: this.connectingErrorCount,
          },
        );

        if (this.connectingErrorCount >= CONNECTING_ERROR_THRESHOLD) {
          otaLog.warn(
            'RemoteOps SSE: muitos erros CONNECTING — forçando reconnect com token novo',
            { connectingErrors: this.connectingErrorCount },
          );
          this.contaReconnect('connecting-errors');
          this.connectingErrorCount = 0;
          this.connectingErrorWindowStart = now;
          if (this.es) {
            this.es.close();
            this.es = null;
          }
          if (!this.explicitlyStopped) {
            this.scheduleReconnect();
          }
        }
      }
    };
  }

  private async buildSseUrl(): Promise<string> {
    const cfg = this.config!;
    // Epic D (Fase D2): o EventSource do RemoteOps é a única conexão SSE e aponta
    // pro endpoint que carrega comandos E conteúdo (/api/sse/device). Auth por
    // JWT SSE via ?token= (com fallback pro deviceToken UUID — ver abaixo).
    const path = `/api/sse/device/${encodeURIComponent(cfg.deviceId)}`;

    // Pentest 2026-06-02 H4 player: tenta pegar SSE token short-lived (60s).
    // Se falhar (rede ruim, endpoint não existe em backend antigo), faz
    // fallback pro deviceToken UUID — preserva compat durante rollout.
    const sseToken = await this.fetchSseToken().catch(() => null);
    // Só renova proativamente quando usamos JWT SSE de verdade; o fallback
    // deviceToken UUID (legacy) não expira, então não precisa de renovação.
    this.usingSseToken = sseToken != null;
    if (!sseToken) {
      // Sem JWT nesta conexão, o vencimento guardado é de um token que não está
      // mais em uso — deixá-lo agendaria uma renovação por um prazo alheio.
      this.sseTokenExpiraEmMs = null;
    }
    const tokenForUrl = sseToken ?? cfg.deviceToken;
    if (!sseToken) {
      // Renato 2026-06-12: device pending = 401 esperado em /sse-token,
      // sem fallback funcional (SSE também falha). Não polui console.
      if (isWaitingDeviceApproval()) {
        otaLog.debug(
          'RemoteOps SSE: device pending approval — sse-token retornou 401 esperado',
        );
      } else {
        console.warn(
          `${LOG} ⚠️ fetchSseToken falhou — usando deviceToken UUID (fallback legacy)`,
        );
        otaLog.warn(
          'RemoteOps SSE: fallback para deviceToken UUID (sse-token endpoint falhou)',
        );
      }
    }
    return `${cfg.apiUrl}${path}?token=${encodeURIComponent(tokenForUrl)}`;
  }

  /**
   * Pega um SSE token short-lived (JWT type=sse, 60s TTL) do backend.
   * Endpoint: POST /api/devices/sse-token
   * Headers: X-Device-Id + X-Device-Token (validados via DeviceTokenAuthService).
   * Retorna null se backend retornar erro ou se o endpoint não existir
   * (4xx/5xx) — caller deve fazer fallback pro deviceToken antigo.
   */
  /**
   * `fetch` com timeout via AbortController. SEM isto, um fetch pendurado (WebKit Tizen
   * degradando / proxy que segura a resposta) trava PRA SEMPRE — e um fetch pendurado NUNCA
   * rejeita, então o `.catch` do caller não pega. Aborta em `timeoutMs` → a Promise rejeita
   * (AbortError) → o caller trata (fallback/retry). Base do fix do SSE morto (2026-07-25).
   */
  private async fetchWithTimeout(
    input: string,
    init: RequestInit,
    timeoutMs: number,
  ): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(input, { ...init, signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
  }

  private async fetchSseToken(): Promise<string | null> {
    const cfg = this.config!;
    try {
      const response = await this.fetchWithTimeout(
        `${cfg.apiUrl}/api/devices/sse-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Device-Id': cfg.deviceId,
            'X-Device-Token': cfg.deviceToken,
          },
        },
        SSE_FETCH_TIMEOUT_MS,
      );
      if (!response.ok) {
        otaLog.debug(
          `RemoteOps SSE: sse-token endpoint retornou ${response.status}`,
        );
        // 🔴 Drena o corpo antes de largar a `Response` — mesma causa-raiz do
        // renderer-kill medida no Tab A7 em 2026-09-18 (vide commit 683464d e
        // PlaybackStatusService): resposta com bytes descartada sem ler nem
        // cancelar mantém vivo o data pipe do Chromium, e no renderer 32-bit
        // isso reserva ~2 MB de address space POR REQUISIÇÃO que nunca volta.
        // Aqui o erro tem corpo de verdade (JSON de erro do server), e um
        // device pending approval toma 401 a CADA tentativa de reconexão — ou
        // seja, é o estado permanente dele.
        await response.body?.cancel().catch(() => undefined);
        // Renato 2026-06-12: 401 quando NÃO estamos pending approval = sinal
        // forte de que o server revogou nosso token (player deletado no admin
        // ou token rotacionado). Dispara recovery — cooldown de 2min no
        // handleServerRevocation evita storm.
        if (response.status === 401 && !isWaitingDeviceApproval()) {
          triggerAuthRecovery('sse-token 401');
        }
        return null;
      }
      const json: { token?: string; expires_in?: number } =
        await response.json();
      if (!json.token) {
        otaLog.warn('RemoteOps SSE: sse-token response sem campo token');
        return null;
      }
      const ttlSec =
        typeof json.expires_in === 'number' && json.expires_in > 0
          ? json.expires_in
          : 180;
      // Vencimento ABSOLUTO: a partir daqui o relógio do token corre sozinho, e
      // nenhum `hello` posterior o move.
      this.sseTokenExpiraEmMs = Date.now() + ttlSec * 1000;
      otaLog.debug('RemoteOps SSE: sse-token obtido', {
        expires_in: json.expires_in,
      });
      return json.token;
    } catch (err) {
      otaLog.debug(`RemoteOps SSE: fetchSseToken falhou — ${String(err)}`);
      return null;
    }
  }

  private scheduleReconnect(): void {
    if (this.explicitlyStopped) return;
    const backoff = Math.min(
      INITIAL_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts),
      MAX_RECONNECT_DELAY_MS,
    );
    // Jitter de até 30% — espalha a reconexão de 6000 players pra não formar
    // thundering herd contra o server quando ele reinicia (todos cairiam e
    // reconectariam no mesmo instante).
    const delay = Math.round(backoff + Math.random() * 0.3 * backoff);
    this.reconnectAttempts += 1;
    console.log(
      `${LOG} ⏳ reconectando em ${delay}ms (tentativa ${this.reconnectAttempts})`,
    );
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      // connect() é async (busca SSE token short-lived antes de abrir EventSource)
      this.connect().catch((err) => {
        otaLog.error('RemoteOps SSE: reconnect connect() lançou', {
          error: String(err),
        });
        console.error(`${LOG} reconnect connect() lançou:`, err);
      });
    }, delay);
  }

  /**
   * Agenda a renovação PROATIVA do token SSE — reconecta com um token FRESCO
   * ANTES de o atual vencer. Elimina o 401 transitório: sem renovação, quando o
   * proxy derruba a conexão e o EventSource nativo reconecta reusando a MESMA
   * URL, o token pode já ter expirado → 401 em loop até o onerror forçar um
   * reconnect. Renovando antes de vencer, o token na URL está sempre válido.
   * Só vale pro JWT SSE (o fallback deviceToken legacy não expira).
   *
   * <p>Chamado a cada `hello`, que chega a cada subscribe — então o prazo é contado
   * a partir do VENCIMENTO do token, não deste instante. Reiniciar um TTL cheio aqui
   * empurrava a renovação pra depois da morte do token e devolvia o player ao
   * caminho reativo (401 → gap → reconnect), onde eventos SSE se perdem para
   * sempre. Vide {@link msAteRenovarToken}.</p>
   */
  private scheduleTokenRenewal(): void {
    if (this.renewalTimer !== null) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }
    // Desligado por medição — vide RENOVACAO_DE_TOKEN_POR_RELOGIO. O token é conferido
    // só no connect, e `connect()` SEMPRE busca um fresco, então o canal segue coberto.
    if (!RENOVACAO_DE_TOKEN_POR_RELOGIO) return;
    if (this.explicitlyStopped || !this.usingSseToken) return;
    const expiraEm = this.sseTokenExpiraEmMs;
    if (expiraEm === null) return;
    const renewInMs = msAteRenovarToken(expiraEm, Date.now());
    this.renewalTimer = window.setTimeout(() => {
      this.renewalTimer = null;
      this.renewConnection();
    }, renewInMs);
    otaLog.debug('RemoteOps SSE: renovação proativa de token agendada', {
      venceEmSec: Math.round((expiraEm - Date.now()) / 1000),
      renewInSec: Math.round(renewInMs / 1000),
    });
  }

  /**
   * Renova a conexão SSE com um token fresco. SEQUENCIAL (connect() fecha a
   * conexão atual e abre a nova) — nunca mantém 2 conexões simultâneas pro mesmo
   * device (evita o "emitter zumbi" no broker — task #78). O blip de reconexão é
   * sub-segundo e raro (~1× a cada ~2,5min); irrelevante pro canal de comandos.
   */
  private renewConnection(): void {
    if (this.explicitlyStopped || !this.config) return;
    otaLog.info(
      'RemoteOps SSE: renovando token proativamente (antes de vencer)',
    );
    console.debug(`${LOG} 🔄 renovando token SSE (proativo, antes de vencer)`);
    // `true` = make-before-break: a conexão atual fica no ar até a nova dizer `hello`.
    this.connect(true).catch((err) => {
      otaLog.error(
        'RemoteOps SSE: renovação proativa falhou — agendando reconnect',
        {
          error: String(err),
        },
      );
      console.error(`${LOG} renovação proativa falhou:`, err);
      this.scheduleReconnect();
    });
  }

  /**
   * Evento nomeado `content` do canal UNIFICADO (Epic D, Fase D2). {@code raw}
   * é o MESMO envelope JSON do Content SSE legado: {@code {"type","data"}}.
   *
   * <p>Reusa a classificação de {@code contentEventDispatch} (fonte única
   * compartilhada com {@code ContentSSEService.handleMessage}) e publica no
   * {@code unifiedContentChannel}, onde o {@code SyncService} liga nos MESMOS
   * handlers de conteúdo que usava com o Content SSE — garantindo comportamento
   * IDÊNTICO. NÃO toca o canal de comando (cmd/hello/ping) nem faz re-sync
   * direto: só encaminha o evento parseado.</p>
   */
  private handleContentEvent(raw: unknown): void {
    const event = parseContentEnvelope(raw);
    if (!event) {
      otaLog.warn('RemoteOps SSE: evento `content` com payload não-JSON', {
        sample: typeof raw === 'string' ? raw.slice(0, 120) : `(${typeof raw})`,
      });
      return;
    }
    // 🔴 `playback_status` NÃO é comando do transporte. Ele é despachado pelo
    // `DeviceCommandService` via `publishContentEventToPlayers`, ou seja, chega AQUI
    // como `content` de `type:"command"` — e não pelo evento nomeado `cmd`, que é do
    // RemoteOps e não tem esse comando no mapa de handlers.
    //
    // Quem respondia era o `ContentSSEService.handleMessage`, da 2ª conexão SSE. Com o
    // canal unificado virando o único caminho, aquele handler deixou de ser alcançado e
    // o pedido sob demanda morreu em silêncio: o admin mandava, nenhum player respondia.
    // Passou despercebido porque o player ainda reportava sozinho a cada 15s — o cache
    // estava sempre fresco e o caminho sob demanda nunca era exercitado. Ao mover a
    // cadência pro servidor (e zerá-la), o painel passou a depender dele e exibiu
    // "Player offline" num player conectado.
    if (event.type === 'command') {
      const dados = (event.data || {}) as Record<string, unknown>;
      if (dados.action === 'playback_status' && typeof dados.requestId === 'string') {
        const requestId = dados.requestId;
        void import('../playback-status/PlaybackStatusService')
          .then(({ default: playbackStatusService }) => {
            // Configura defensivamente, como o handler do ContentSSE fazia: o Stream
            // também configura no mount, mas um comando que chegue antes disso
            // encontraria o serviço sem apiUrl/deviceId e sairia sem reportar.
            if (this.config) {
              playbackStatusService.configure(
                this.config.apiUrl,
                this.config.deviceId,
              );
            }
            return playbackStatusService.report(requestId);
          })
          .catch((err) =>
            otaLog.warn('RemoteOps: falha ao responder playback_status', {
              erro: err instanceof Error ? err.message : String(err),
            }),
          );
      }
      return; // comando não é conteúdo — não propaga pro canal de conteúdo
    }

    // heartbeat/connected/command do TRANSPORTE chegam como eventos NOMEADOS
    // (ping/hello/cmd), não pelo `content` — aqui só passam os tipos de conteúdo
    // que o Content SSE sempre encaminhou (re-sync/config/orientação/trigger).
    if (!isForwardedContentEventType(event.type)) {
      return;
    }
    unifiedContentChannel.emit(event);
  }

  private async handleCmdEvent(raw: unknown): Promise<void> {
    if (typeof raw !== 'string') {
      otaLog.warn('RemoteOps handleCmdEvent: raw não-string', {
        type: typeof raw,
      });
      return;
    }
    let parsed: IncomingCmd;
    try {
      parsed = JSON.parse(raw) as IncomingCmd;
    } catch {
      otaLog.warn('RemoteOps handleCmdEvent: payload não-JSON', {
        sample: String(raw).slice(0, 100),
      });
      console.warn(
        `${LOG} payload "cmd" não-JSON: ${String(raw).slice(0, 100)}`,
      );
      return;
    }

    otaLog.info('RemoteOps handleCmdEvent: comando parsed', {
      cmdId: parsed.cmdId,
      command: parsed.command,
      hasParams: !!parsed.params,
    });

    if (!parsed.cmdId || !parsed.command) {
      otaLog.warn('RemoteOps handleCmdEvent: cmd inválido', { parsed });
      console.warn(`${LOG} cmd inválido (faltando cmdId ou command):`, parsed);
      return;
    }

    const handler = this.handlers.get(parsed.command);
    if (!handler) {
      otaLog.warn('RemoteOps handleCmdEvent: comando sem handler registrado', {
        command: parsed.command,
        registeredCommands: Array.from(this.handlers.keys()),
      });
      // P5: sendCmdResult é async; sem await fica fire-and-forget. Tem
      // try/catch interno, mas vamos paranóicos com .catch() pra garantir
      // que nunca vira unhandled-rejection se o contrato mudar.
      this.sendCmdResult(
        parsed.cmdId,
        'unsupported',
        null,
        `command not supported: ${parsed.command}`,
      ).catch((err) =>
        console.warn(`${LOG} sendCmdResult(unsupported) failed:`, err),
      );
      return;
    }

    try {
      otaLog.info('RemoteOps handleCmdEvent: chamando handler', {
        cmdId: parsed.cmdId,
        command: parsed.command,
      });
      const params = parsed.params ?? {};
      const outcome = await handler(parsed.cmdId, params);
      otaLog.info('RemoteOps handleCmdEvent: handler retornou', {
        cmdId: parsed.cmdId,
        command: parsed.command,
        status: outcome.status,
        hasError: !!outcome.error,
      });
      this.sendCmdResult(
        parsed.cmdId,
        outcome.status,
        outcome.result,
        outcome.error,
      ).catch((err) =>
        console.warn(`${LOG} sendCmdResult(${outcome.status}) failed:`, err),
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      otaLog.error('RemoteOps handleCmdEvent: handler THREW', {
        cmdId: parsed.cmdId,
        command: parsed.command,
        error: msg,
      });
      console.error(`${LOG} handler error cmd=${parsed.command}:`, err);
      this.sendCmdResult(parsed.cmdId, 'failed', null, msg).catch((cerr) =>
        console.warn(`${LOG} sendCmdResult(failed) failed:`, cerr),
      );
    }
  }

  /**
   * Envia cmdResult via REST POST. Falha silenciosa (apenas log) — admin
   * vai esperar timeout. Não bloqueamos o flow do player.
   */
  private async sendCmdResult(
    cmdId: string,
    status: 'success' | 'failed' | 'unsupported',
    result: unknown,
    error: string | null | undefined,
  ): Promise<void> {
    if (!this.config) return;
    const body: Record<string, unknown> = { status };
    if (result !== null && result !== undefined) body.result = result;
    if (error) body.error = error;

    const url = `${this.config.apiUrl}/api/v1/remote-player/devices/${encodeURIComponent(this.config.deviceId)}/commands/${encodeURIComponent(cmdId)}/result`;
    try {
      const res = await this.fetchWithTimeout(
        url,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Device-Token': this.config.deviceToken,
          },
          body: JSON.stringify(body),
        },
        SSE_FETCH_TIMEOUT_MS,
      );
      if (!res.ok) {
        console.warn(
          `${LOG} sendCmdResult: status ${res.status} pra cmdId=${cmdId}`,
        );
      }
      // 🔴 Só lemos o `status` — sem isto a `Response` (e o corpo de ack do
      // server) é largada sem ler nem cancelar, o que segura o data pipe do
      // Chromium e vaza address space no renderer 32-bit. Mesma causa-raiz do
      // renderer-kill do embedded (vide fetchSseToken acima e commit 683464d).
      // Pareia 1:1 com o snapshot: até 60/min com live view aberto no admin.
      await res.body?.cancel().catch(() => undefined);
    } catch (err) {
      console.warn(`${LOG} sendCmdResult network error cmdId=${cmdId}:`, err);
    }
  }

  /**
   * Envia snapshot via REST POST. Same fail-soft strategy.
   *
   * 2026-05-29 — payload tipado em snake_case pra alinhar com convenção
   * do backend (Jackson SNAKE_CASE global + @JsonProperty explícitos no
   * SnapshotPayload.java). Antes o tipo tinha mistura inconsistente:
   * outer `playback_context` snake + inner contentId/contentName/etc
   * camel — gerava TS2345 quando o call site (linha ~335) populava com
   * snake_case correto.
   */
  private async sendSnapshotEvent(payload: {
    image_base64: string;
    format: string;
    width: number;
    height: number;
    playback_context?: {
      mode: string;
      content_id: string;
      content_name: string;
      position_sec: number;
      duration_sec: number;
    };
  }): Promise<void> {
    if (!this.config) return;
    const url = `${this.config.apiUrl}/api/v1/remote-player/devices/${encodeURIComponent(this.config.deviceId)}/snapshot/event`;
    try {
      const res = await this.fetchWithTimeout(
        url,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Device-Token': this.config.deviceToken,
          },
          body: JSON.stringify(payload),
        },
        SSE_FETCH_TIMEOUT_MS,
      );
      if (!res.ok) {
        console.warn(`${LOG} sendSnapshot: status ${res.status}`);
      }
      // 🔴 Idem sendCmdResult: só o `status` é lido. Este é o caminho de MAIOR
      // frequência do player — enquanto um admin segura a live view aberta são
      // até 60 POSTs/min, cada um vazando ~2 MB de address space no renderer
      // 32-bit se a `Response` for largada sem drenar.
      await res.body?.cancel().catch(() => undefined);
    } catch (err) {
      console.warn(`${LOG} sendSnapshot network error:`, err);
    }
  }

  // ----------------------------------------------------------------------
  //  Handlers
  // ----------------------------------------------------------------------

  /**
   * Captura snapshot via html2canvas e envia DOIS payloads:
   *   1. POST /snapshot/event — alimenta cache no broker + broadcast pros admins.
   *   2. cmdResult success — fecha o ciclo do comando.
   */
  private async handleSnapshotCapture(): Promise<HandlerResult> {
    if (this.snapshotCaptureInFlight) {
      const playback = this.resolveCurrentPlaybackContext();
      otaLog.info('snapshot:capture skipped (capture in flight)');
      return {
        status: 'success',
        result: {
          throttled: true,
          remaining_sec: 1,
          reason: 'capture_in_flight',
          playback_context: playback,
        },
      };
    }
    const task = this.handleSnapshotCaptureInner();
    this.snapshotCaptureInFlight = task;
    try {
      return await task;
    } finally {
      if (this.snapshotCaptureInFlight === task) {
        this.snapshotCaptureInFlight = null;
      }
    }
  }

  private async handleSnapshotCaptureInner(): Promise<HandlerResult> {
    const isTizen =
      typeof window !== 'undefined' &&
      typeof (window as { tizen?: unknown }).tizen !== 'undefined';
    const isAndroid =
      (window as unknown as { __effluxPlatform?: { platform?: string } })
        .__effluxPlatform?.platform === 'android';

    if (isAndroid) {
      const sinceLast = Date.now() - this.lastSnapshotAt;
      if (
        this.lastSnapshotAt > 0 &&
        sinceLast < ANDROID_SNAPSHOT_MIN_INTERVAL_MS
      ) {
        const playback = this.resolveCurrentPlaybackContext();
        otaLog.info('snapshot:capture throttled (android)', {
          sinceLastMs: sinceLast,
          minMs: ANDROID_SNAPSHOT_MIN_INTERVAL_MS,
        });
        return {
          status: 'success',
          result: {
            throttled: true,
            remaining_sec: Math.ceil(
              (ANDROID_SNAPSHOT_MIN_INTERVAL_MS - sinceLast) / 1000,
            ),
            playback_context: playback,
          },
        };
      }
      this.lastSnapshotAt = Date.now();
    }

    if (isTizen) {
      const sinceLast = Date.now() - this.lastSnapshotAt;
      const minInterval = !syncStore.hasContent
        ? TIZEN_SNAPSHOT_MIN_INTERVAL_MS * 2
        : TIZEN_SNAPSHOT_MIN_INTERVAL_MS;
      if (this.lastSnapshotAt > 0 && sinceLast < minInterval) {
        const playback = this.resolveCurrentPlaybackContext();
        return {
          status: 'success',
          result: {
            throttled: true,
            remaining_sec: Math.ceil((minInterval - sinceLast) / 1000),
            playback_context: playback,
          },
        };
      }
      this.lastSnapshotAt = Date.now();
    }

    const capture = await captureScreenBase64();
    if (!capture) {
      return {
        status: 'failed',
        error: 'capture returned null (tainted canvas or OOM)',
      };
    }
    await this.sendSnapshotEvent({
      image_base64: capture.imageBase64,
      format: capture.format,
      width: capture.width,
      height: capture.height,
      ...(capture.diagnostics?.playbackContext
        ? {
            playback_context: {
              mode: capture.diagnostics.playbackContext.mode,
              content_id: capture.diagnostics.playbackContext.contentId,
              content_name: capture.diagnostics.playbackContext.contentName,
              position_sec: capture.diagnostics.playbackContext.positionSec,
              duration_sec: capture.diagnostics.playbackContext.durationSec,
            },
          }
        : {}),
    });
    // Diagnostics retornam no cmdResult — broker forwarda pro admin via WS.
    // Útil pra debugar snapshots pretos em TV (Tizen 4.0 WebView).
    return {
      status: 'success',
      result: {
        width: capture.width,
        height: capture.height,
        format: capture.format,
        diagnostics: capture.diagnostics ?? null,
        playback_context: capture.diagnostics?.playbackContext ?? null,
      },
    };
  }

  /** playback_context leve quando throttle Tizen — sync de tempo sem html2canvas. */
  private resolveCurrentPlaybackContext(): PlaybackContext | null {
    // O PRIMEIRO <video> do DOM pode ser o standby do motor A/B — pausado, invisível
    // e sem `data-content-id`. Pegá-lo fazia o contexto cair no fallback do
    // streamStore (relógio de parede) em vez do `currentTime` da mídia: não ficava
    // errado, ficava MENOS PRECISO, e em silêncio. Escolhe o que está na tela.
    const container = document.getElementById('stream-container');
    const candidatos = Array.from(
      (container ?? document).querySelectorAll<HTMLVideoElement>('video'),
    ).filter(isVideoOnScreen);
    const video =
      candidatos.find((v) => resolveVideoContentId(v)) ?? candidatos[0];
    if (video) {
      const contentId = resolveVideoContentId(video);
      if (contentId) return resolvePlaybackContext(contentId, video);
    }

    const media = streamStore.currentMedia;
    if (!media?.id) return null;

    const startedAt = streamStore.currentItemStartedAt;
    const positionSec =
      startedAt && Number.isFinite(startedAt)
        ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
        : 0;
    const durationSec = Math.max(0, Math.floor(media.duration ?? 0));

    return {
      mode: 'logical_fallback',
      contentId: media.id,
      contentName: media.name?.trim() || 'Vídeo',
      positionSec,
      durationSec,
    };
  }

  /**
   * `app:restart` — reinicia o PROCESSO onde a plataforma souber fazer isso.
   *
   * <p>Até 1.0.452 este comando era um alias de `app:reload`: os dois caíam no
   * mesmo `handleAppReload` e faziam `location.reload()`. O admin tinha dois
   * botões com o mesmo efeito, e o rótulo "Reiniciar app" mentia.</p>
   *
   * <p><b>Por que importa.</b> Medido em 04/10 na ANL-Player-TV: um reload de
   * página devolve <b>0 MB</b> de memória nativa (367 → 366 MB, e 366 ainda
   * 19 min depois), enquanto a TV perde ~25 MB por ciclo de 24 h. O vazamento
   * vive fora da página — recarregar o documento não o alcança. Só um processo
   * novo pode alcançar. Vide
   * {@code docs/roadmaps/FEATURE-TIZEN-VAZAMENTO-AVPLAY.md}.</p>
   *
   * <p><b>Tizen e Android</b> (decisão Renato 2026-10-04). Cada uma mata o
   * processo do seu jeito e cada uma agenda a própria volta:</p>
   *
   * <ul>
   *   <li><b>Tizen</b> — `AlarmRelative(2s)` + `tizen.application.getCurrentApplication().exit()`</li>
   *   <li><b>Android</b> — `AlarmManager` + `exit(0)` no plugin nativo
   *       (`OTAPlugin.restartApp` → `AppRestarter`). Até a 1.0.452 o Android
   *       chamava `Capacitor.Plugins.App.restart()`, um método que não existe
   *       (o pacote `@capacitor/app` nem está instalado): o "restart" do
   *       Android era, na prática, o mesmo reload.</li>
   * </ul>
   *
   * <p>A capacidade é `restartProcess()`, separada do `restartApp()` do fluxo
   * de OTA justamente porque no Android os dois têm que ser diferentes: o do
   * OTA precisa RECARREGAR, nunca matar, senão derruba o diálogo do
   * PackageInstaller e recria o loop de install de 2026-05-28.</p>
   *
   * <p><b>Embedded e Browser continuam no reload</b>, e isso não é lacuna: o
   * embedded roda dentro do app do PARCEIRO — matar o processo mataria o app
   * do cliente junto. Onde não há processo próprio pra reiniciar, o comando
   * recarrega e <b>diz isso</b> no resultado (`mode: 'page'` + `reason`), em
   * vez de fingir que reiniciou.</p>
   *
   * <p>⚠️ <b>O risco está documentado e é real.</b> Matar o processo é a parte
   * fácil; o risco é não voltar. Na Tizen, nos dados de 5 dias de 2026-07, um
   * `exit()` sem alarme não voltou em 6 de 10 mortes (app apagado de 15 min a
   * 10 h) — por isso o alarme é agendado ANTES de sair, nas duas plataformas,
   * e no Android o `AppRestarter` se recusa a matar o processo se não
   * conseguir agendar a volta. No Android ainda há a rede extra de ser Device
   * Owner e Home persistente (RK18): o sistema não fica sem Home.</p>
   */
  private async handleAppRestart(cmdId: string): Promise<HandlerResult> {
    const plataforma = window.__effluxPlatform;
    const nome = plataforma?.platform;

    // O gate é a CAPACIDADE, não uma lista de nomes: só Tizen e Android
    // implementam `restartProcess`. Embedded e Browser não implementam de
    // propósito (vide IPlatformService), e um build antigo também não tem —
    // os três caem no reload pelo mesmo caminho, sem caso especial.
    if (typeof plataforma?.restartProcess !== 'function') {
      const r = await this.handleAppReload(cmdId);
      return {
        ...r,
        result: {
          ...(r.result as Record<string, unknown>),
          mode: 'page',
          reason: `restart de processo não disponível em "${nome ?? 'desconhecido'}"`,
        },
      };
    }

    // Mesmo cuidado do reload: não cortar um snapshot em voo.
    if (this.snapshotCaptureInFlight) {
      otaLog.info('app:restart: aguardando snapshot em voo', { cmdId });
      try {
        await Promise.race([
          this.snapshotCaptureInFlight,
          new Promise<void>((resolve) => setTimeout(resolve, 8000)),
        ]);
      } catch {
        /* snapshot falhou — segue o restart mesmo assim */
      }
    }

    otaLog.info('app:restart: reiniciando o PROCESSO', { cmdId, nome });
    // O resultado é postado pelo chamador logo depois deste return, por um POST
    // que NÃO é aguardado. Esta espera é o que dá a esse POST tempo de sair
    // antes do processo morrer — com 150 ms a resposta se perdia na rede da
    // loja e o admin mostrava timeout num restart que tinha funcionado.
    setTimeout(() => {
      try {
        plataforma.restartProcess!();
      } catch (err) {
        console.warn(`${LOG} restartProcess falhou, caindo pro reload:`, err);
        window.location.reload();
      }
    }, RESTART_GRACE_MS);

    return {
      status: 'success',
      result: {
        mode: 'process',
        platform: nome,
        scheduled_in_ms: RESTART_GRACE_MS,
        // O texto é por plataforma porque o mecanismo é por plataforma, e este
        // campo é lido pelo operador no admin. Dizer "alarme" no Android seria
        // falso desde o trampolim: lá o alarme só existe como rede de segurança
        // e, no caminho normal, é cancelado sem nunca disparar.
        note:
          nome === 'android'
            ? 'processo morto e relançado na hora por um trampolim em processo separado'
            : 'o app sai e o sistema relança pelo alarme agendado antes da saída',
      },
    };
  }

  /** app:reload — reload seguro (teardown vídeo + aguarda snapshot). */
  private async handleAppReload(cmdId: string): Promise<HandlerResult> {
    if (this.snapshotCaptureInFlight) {
      otaLog.info('app:reload: aguardando snapshot em voo', { cmdId });
      try {
        await Promise.race([
          this.snapshotCaptureInFlight,
          new Promise<void>((resolve) => setTimeout(resolve, 8000)),
        ]);
      } catch {
        /* snapshot failed — segue reload mesmo assim */
      }
    }

    const delayMs = getSafeReloadDelayMs();
    void executeSafePageReload(cmdId).catch((err: unknown) => {
      console.warn(`${LOG} safe reload failed, forcing reload:`, err);
      window.location.reload();
    });

    return {
      status: 'success',
      result: {
        scheduled_in_ms: delayMs + 150,
        safe_reload: true,
      },
    };
  }

  /** Health report básico — info do navigator + memória (quando disponível). */
  /**
   * Coleta as métricas da Fase 1 da Saúde do Player.
   *
   * ORÇAMENTO É REQUISITO, não detalhe. Duas armadilhas evitadas de
   * propósito, as duas aprendidas na marra em 19-20/09:
   *
   *  - NÃO conta pendentes pelo índice. `index.count(only(1))` ocupava ~60 s
   *    da thread de IndexedDB no Tab A7 e foi justamente o que tiramos do
   *    caminho do flush. `AnalyticsDB.getHealthMetrics` usa `store.count()`,
   *    que responde em 2 ms num banco saudável — e com o `deleteSynced` o
   *    total do store já é praticamente o número de pendentes.
   *  - NÃO varre o cache de mídia arquivo por arquivo. Cada `stat` cruza a
   *    ponte do Capacitor; com ~160 vídeos seriam 160 travessias. O cache já
   *    mantém o índice em localStorage — leitura de graça.
   *
   * Best-effort inteiro: qualquer falha vira `null` no campo, nunca derruba
   * o relatório.
   */
  /**
   * Bloco do AVPlay — só quando faz sentido.
   *
   * O AVPlay é o backend de vídeo da Tizen; em Android e browser ele não
   * existe, e os campos vinham todos zerados. As séries colunares
   * (`blackbox`) são o grosso do peso: até 720 amostras × 8 colunas cada,
   * e há duas (a atual e a da sessão anterior). Ficam atrás de um parâmetro
   * porque só servem pra investigar leak de decoder na TV.
   */
  private blocoAvplay(
    readJson: (k: string) => unknown,
    columnar: (k: string) => unknown,
    incluirBlackbox: boolean,
  ): Record<string, unknown> {
    // Gate pela PLATAFORMA, não pela presença da chave.
    //
    // A primeira versão disto checava se `efflux_avplay_counters_v1` existia,
    // supondo que só a Tizen gravaria. ERRADO: puxando o dado real de um Tab
    // A7 (2026-09-20) a chave estava lá, zerada — e o bloco continuaria indo.
    // O teste não pegou porque removia a chave antes de checar: validava a
    // lógica do código e ignorava a realidade do device.
    if (
      (window as unknown as { __effluxPlatform?: { platform?: string } })
        .__effluxPlatform?.platform !== 'tizen'
    ) {
      return {};
    }

    const counters = readJson('efflux_avplay_counters_v1');

    return {
      avplay_counters: counters,
      avplay_timing: readJson('efflux_avplay_timing_v1'),
      avplay_recycles: Number(
        window.localStorage?.getItem('efflux_avplay_recycles_v1') ?? 0,
      ),
      ...(incluirBlackbox
        ? {
            avplay_blackbox: columnar('efflux_avplay_blackbox_v1'),
            avplay_blackbox_prev: columnar('efflux_avplay_blackbox_prev_v1'),
          }
        : {}),
    };
  }

  private async coletarSaude(): Promise<Record<string, unknown>> {
    const idb = await analyticsDB
      .getHealthMetrics()
      .catch(() => ({ records: null, readMs: null, loopLagMs: null }));

    // Índice do cache de mídia, pela interface `IMediaCache.getMetadata()`.
    //
    // ⚠️ Isto lia `efflux_capacitor_fs_metadata_v2` direto do localStorage —
    // chave que só existe no ANDROID. Na Tizen é `efflux_tizen_media_metadata_v{N}`
    // e no embedded é `efflux_embedded_media_metadata_v1`, então os dois campos
    // abaixo chegavam NULL na TV e no embedded, em silêncio. Não voltar a ler
    // chave de plataforma aqui — `coletarCacheDeMidia` serve as quatro.
    const cacheDeMidia = coletarCacheDeMidia();

    // O PlatformService vive em `window.__effluxPlatform` (registrado pelo
    // PlatformProvider) — mesmo caminho que o `device:reboot` usa. Só o
    // Android implementa `listTempFiles`; nas demais plataformas o campo
    // fica null, que é honesto: elas não gravam bundle em disco.
    let bundlesOta: number | null = null;
    try {
      const plataforma = window.__effluxPlatform;
      const listar = plataforma?.listTempFiles;
      if (listar) {
        const nomes: string[] = await listar.call(plataforma);
        bundlesOta = nomes.filter(
          (n) => n.startsWith('ota_') && n.endsWith('.bundle'),
        ).length;
      }
    } catch {
      bundlesOta = null;
    }

    const up = getUptime();
    const ultimoReload = Number(
      window.localStorage?.getItem(LAST_RELOAD_AT_KEY) ?? '',
    );

    return {
      idb: {
        records: idb.records,
        read_ms: idb.readMs,
        // Atraso do event loop na MESMA janela. Vide AnalyticsDB.getHealthMetrics.
        loop_lag_ms: idb.loopLagMs,
      },
      engine: detectEngineVersion(),
      storage: {
        media_cache_files: cacheDeMidia.files,
        media_cache_mb: cacheDeMidia.mb,
        // O denominador do "N de M": o coletor sabe o que chegou ao disco, o
        // store sabe o que a programação pediu. Aqui o store está à mão.
        media_cache_total: mediaStore.cacheStatus.size || null,
        media_cache_pending: mediaStore.pendingDownloads,
        ota_bundles: bundlesOta,
      },
      uptime: {
        page_ms: up.pageMs,
        process_ms: up.processMs,
        reloaded_in_same_process: up.reloadedInSameProcess,
      },
      reload: {
        last_at:
          Number.isFinite(ultimoReload) && ultimoReload > 0
            ? ultimoReload
            : null,
      },
      sse: this.blocoSse(up.pageMs),
    };
  }

  /**
   * Telemetria do canal SSE de RemoteOps desde o boot do player. `zombie` é o
   * número que responde "com que frequência a conexão morre calada?" — só
   * conta as causas de watchdog, porque `network-online`/`visibility-visible`
   * são reconexões esperadas e inflariam a taxa. Vide {@link reconnectCounts}.
   *
   * @param pageMs uptime da página, pra derivar taxa por hora sem o servidor
   *               precisar guardar a série.
   */
  private blocoSse(pageMs: number): Record<string, unknown> {
    const porCausa: Record<string, number> = {};
    for (const [causa, n] of this.reconnectCounts) porCausa[causa] = n;

    const zumbis =
      (this.reconnectCounts.get('watchdog-silence') ?? 0) +
      (this.reconnectCounts.get('watchdog-dead-channel') ?? 0);
    const horas = pageMs > 0 ? pageMs / 3_600_000 : 0;

    return {
      // Reconexões que indicam canal MORTO sem o EventSource perceber.
      zombie: zumbis,
      // Normalizado: compara players com uptimes diferentes. null se o uptime
      // ainda é curto demais pra taxa significar algo (< 5 min).
      zombie_per_hour:
        horas >= 1 / 12 ? Math.round((zumbis / horas) * 100) / 100 : null,
      // Todas as causas, inclusive as proativas — pra ver o quadro completo.
      reconnects: porCausa,
      last_at: this.reconnectLastAt.get('watchdog-silence') ?? null,
      // Estado AGORA: separa "nunca conectou" de "conectado e saudável".
      connected: this.es?.readyState === 1,
      silent_ms:
        this.lastActivityAt > 0 ? Date.now() - this.lastActivityAt : null,
    };
  }

  /**
   * Relatório de saúde. Por padrão envia só o que é RELEVANTE pra plataforma
   * do device — vide `blocoAvplay`. Passe `{"blackbox": true}` nos params pra
   * incluir as séries brutas do AVPlay (pesadas, só úteis em diagnóstico de
   * TV).
   */
  private async handleHealthReport(
    _cmdId: string,
    params: Record<string, unknown> = {},
  ): Promise<HandlerResult> {
    const perfMem = (
      performance as Performance & {
        memory?: { usedJSHeapSize?: number; totalJSHeapSize?: number };
      }
    ).memory;
    // RAM livre do APARELHO — proxy direto do leak nativo/CMA: puxando isto ao
    // vivo a gente vê a memória cair sem esperar o crash.
    //
    // Pelo IPlatformService, NÃO por `tizen.systeminfo` direto: a TV mede por
    // systeminfo, o embedded pela bridge do host e o Android pelo plugin. Ler a
    // API da Tizen aqui fazia o "Medir agora" sair sem RAM em tudo que não é TV.
    const memoriaSistema = await coletarMemoriaDoSistema();
    // Contadores + trend de latência do AVPlay via localStorage (mesmo bridge do crashCapture —
    // sem import de código de plataforma). Deixa o health:report mostrar opens/closes + latência.
    const readJson = (key: string): Record<string, number> | null => {
      try {
        const raw = window.localStorage?.getItem(key);
        return raw ? (JSON.parse(raw) as Record<string, number>) : null;
      } catch {
        return null;
      }
    };
    /** Igual ao `readJson`, mas pros rings (arrays) — devolve `[]` em vez de `null`. */
    const readRing = (key: string): unknown[] => {
      try {
        const raw = window.localStorage?.getItem(key);
        const parsed = raw ? (JSON.parse(raw) as unknown) : null;
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };
    // Caixa-preta de longo horizonte (Fase 0): curva opens × RAM-livre × heap × playtime ao longo
    // da sessão. Devolvida em COLUNAS (sem repetir chaves por amostra) e capada às últimas 360
    // (~6h @ 60s) pra caber no cmdResult. `_prev` = sessão anterior (curva completa pós-crash).
    const columnar = (key: string): Record<string, Array<number | null>> => {
      const cols: Record<string, Array<number | null>> = {
        t: [],
        o: [],
        c: [],
        e: [],
        av: [],
        hp: [],
        pt: [],
        r: [], // C4: recycles acumulados (step ↑ = remount dos planos)
      };
      try {
        const raw = window.localStorage?.getItem(key);
        const arr = raw
          ? (JSON.parse(raw) as Array<Record<string, number>>)
          : [];
        for (const r of (Array.isArray(arr) ? arr : []).slice(-360)) {
          cols.t.push(r?.t ?? null);
          cols.o.push(r?.o ?? null);
          cols.c.push(r?.c ?? null);
          cols.e.push(r?.e ?? null);
          cols.av.push(r?.av ?? null);
          cols.hp.push(r?.hp ?? null);
          cols.pt.push(r?.pt ?? null);
          cols.r.push(r?.r ?? null);
        }
      } catch {
        /* ignore */
      }
      return cols;
    };
    return {
      status: 'success',
      result: {
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        online: navigator.onLine,
        // A versão do APP (não a do WebView, que vai em `engine`). O servidor
        // grava o health:report como amostra da série quando o operador clica
        // "Medir agora"; sem este campo, aquela amostra entraria sem versão e a
        // tela teria que adivinhar a partir de uma amostra anterior.
        app_version: import.meta.env.VITE_APP_VERSION ?? null,
        // Mesmo motivo do `app_version`: o "Medir agora" vira amostra da série, e sem este
        // campo o cartão da versão perderia o número de decoders ao medir sob demanda.
        video_decoders: lerDecodersDeVideoEmUso(),
        memory_used_mb: perfMem?.usedJSHeapSize
          ? Math.round(perfMem.usedJSHeapSize / 1024 / 1024)
          : null,
        memory_total_mb: perfMem?.totalJSHeapSize
          ? Math.round(perfMem.totalJSHeapSize / 1024 / 1024)
          : null,
        // RAM do APARELHO. Vem do IPlatformService, não de `tizen.systeminfo`:
        // cada plataforma mede como pode (systeminfo na TV, bridge do host no
        // embedded, plugin no Android), e ler a API da Tizen direto fazia o
        // "Medir agora" sair SEM RAM em tudo que não é TV — enquanto o sync
        // periódico, que já usava a abstração, trazia. O resultado na tela era
        // uma amostra órfã a cada clique, quebrando a linha do gráfico em
        // pedaços de um ponto (que nem chegam a ser desenhados).
        ...(memoriaSistema.freeMb !== null
          ? {
              native_avail_mb: memoriaSistema.freeMb,
              native_total_mb: memoriaSistema.totalMb,
            }
          : {}),

        // AVPlay é EXCLUSIVO da Tizen. Num tablet Android este bloco inteiro
        // vinha zerado — contadores em 0, timing em 0 e duas séries colunares
        // de centenas de amostras só com zeros e nulls, somando dezenas de KB
        // contra ~5 KB de dado útil. Agora só aparece onde existe, e as séries
        // brutas ficam atrás de `params.blackbox` porque são pesadas até na TV.
        ...this.blocoAvplay(readJson, columnar, Boolean(params.blackbox)),

        // Crashes sobrevivem a reboot (localStorage) e NUNCA são apagados —
        // o `markCrashesReported` só avança uma marca d'água. São no máximo
        // 20. Ter isso aqui evita ter que cruzar com os eventos do servidor
        // pra saber se o player vem morrendo.
        crashes: readRing('efflux_crash_log_v1').slice(-5),
        crashes_total: readRing('efflux_crash_log_v1').length,
        // Refresh periódico (mitigação do leak): histórico dos reloads + estado das flags. Revela o
        // sawtooth — se a RAM livre no momento de cada reload fica CONSTANTE (renovou o processo)
        // ou DECLINA reload a reload (não renovou → só piscou preto). O `reason` de cada entrada
        // separa as duas curvas (tempo vs backstop de RAM).
        //
        // Orçamento de tempo do preto (2026-07-28): `boot_timing` é o que quebra o preto em blocos
        // (bundle → react → playlist → open → frame) e `black_log` é a duração medida. Sem eles a
        // leitura só existe via CDP, que depende de dev mode ligado na TV.
        //
        // `readRing` pros ARRAYS (devolve `[]` em vez de `null`) e `readJson` pro objeto de flags.
        reload_log: readRing('efflux_avplay_reload_log_v1'),
        boot_timing: readRing('efflux_boot_timing_v1'),
        black_log: readRing('efflux_black_log_v1'),
        changes: readJson('efflux_change_state_v1'),
        viewport: { width: window.innerWidth, height: window.innerHeight },

        // --- Fase 1 da Saúde do Player (Renato 2026-09-20) ---------------
        // As métricas que faltaram na investigação do acúmulo de analytics.
        // A RD-Raia rodava com 210.404 registros e leitura de 9.550 ms
        // ENTREGANDO os eventos em dia: nenhum indicador do servidor via.
        // Tudo aqui é barato de propósito — vide `coletarSaude`.
        ...(await this.coletarSaude()),

        timestamp: Date.now(),
      },
    };
  }

  /**
   * device:reboot — delega pro {@link IPlatformService.restart}('reboot_device')
   * registrado em {@code window.__effluxPlatform} pelo {@code PlatformProvider}.
   *
   * <p>Em Android com Device Owner provisionado, o plugin nativo {@code KioskMode}
   * resolve a Promise imediatamente e agenda {@code dpm.reboot()} 500ms depois —
   * tempo suficiente pro cmdResult sair via fetch antes do device reiniciar.</p>
   *
   * <p>Em qualquer outro caso (browser, TV Tizen/webOS, Android sem Device Owner,
   * build sem plugin nativo) o handler retorna {@code unsupported} com motivo
   * específico — ver {@link #explainRebootUnsupported}.</p>
   */
  private async handleDeviceReboot(cmdId: string): Promise<HandlerResult> {
    const platform = window.__effluxPlatform;
    if (!platform) {
      return {
        status: 'unsupported',
        error: 'PlatformService não inicializado (PlatformProvider ausente)',
      };
    }

    try {
      // Proteção D: marca o reboot comandado ANTES do dpm.reboot (o device morre abruptamente,
      // sem beforeunload) → o boot seguinte reconhece o restart e suprime o falso silent_crash.
      markExpectedRestart();
      await platform.restart('reboot_device');
      console.log(`${LOG} 🔁 reboot_device agendado pelo nativo cmd=${cmdId}`);
      return {
        status: 'success',
        result: { scheduled: true, platform: platform.platform },
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.startsWith('unsupported:')) {
        return {
          status: 'unsupported',
          error: this.explainRebootUnsupported(platform.platform, msg),
        };
      }
      return { status: 'failed', error: msg };
    }
  }

  /**
   * Traduz a mensagem técnica de `unsupported:` do PlatformService em algo
   * acionável pro operador no admin. Os 3 cenários previstos hoje:
   *
   * <ol>
   *   <li>Plataforma não-Android (Tizen, webOS, browser) — SO não expõe reboot
   *       a apps de terceiros. Sem caminho viável.</li>
   *   <li>Android sem plugin KioskMode — build APK foi feito sem o plugin
   *       custom (dev/preview).</li>
   *   <li>Android com plugin mas sem Device Owner — tablet stock Android.
   *       Requer provisionamento via ADB (`dpm set-device-owner`) ou QR no
   *       setup inicial. Reboot programático via DPM é a única via legítima
   *       no Android.</li>
   * </ol>
   */
  private explainRebootUnsupported(
    platform: Platform,
    originalMsg: string,
  ): string {
    if (platform !== 'android') {
      return `device:reboot só disponível em Android com Device Owner (plataforma atual: ${platform})`;
    }
    if (originalMsg.includes('KioskMode plugin indisponível')) {
      return 'device:reboot — plugin nativo KioskMode ausente (build APK sem o plugin custom)';
    }
    if (originalMsg.includes('backend não suporta')) {
      return 'device:reboot — tablet não está provisionado como Device Owner. Provisione via ADB (dpm set-device-owner) ou QR no setup inicial.';
    }
    return originalMsg;
  }

  /**
   * cache:clear — limpa cache de mídia (vídeos/imagens) e metadata órfã,
   * forçando re-download no próximo sync.
   *
   * <p>Backend de cache é resolvido pelo {@link createMediaCache} factory:
   * {@code TizenMediaCache} (Filesystem) em TV, {@code IndexedDBCache} em
   * Android, {@code MediaCache} (Cache API) em browser/PWA, {@code NoopCache}
   * como fallback. Todos implementam {@code IMediaCache.clear()} desde Q4/2024.</p>
   *
   * <p>Cenário de uso: tablet com mídia corrompida tocando vídeo travado.
   * Em vez de deslocar técnico até a loja, operador remoto manda este comando
   * → cache esvazia → {@code syncStore.forceResync()} dispara → mídia volta
   * limpa do server em ~30s.</p>
   *
   * <p><b>Não limpa:</b> auth (deviceToken/deviceId), config (kiosk_pin,
   * orientation, prefs). Só cache de mídia + metadata associada — pra não
   * exigir re-aprovação do device após o comando.</p>
   */
  private async handleCacheClear(cmdId: string): Promise<HandlerResult> {
    try {
      const cache = await createMediaCache();
      const before = await cache
        .getStorageInfo()
        .catch(() => ({ usage: 0, quota: 0 }));
      const itemCount = Object.keys(cache.getMetadata() ?? {}).length;

      // 2026-05-31 — Renato observou em prod: cache:clear limpa cache mas
      // tablet/TV ficam tocando via stream online por horas sem baixar de
      // novo. Causa raiz: items "fantasma" na DownloadQueue de uma sessão
      // anterior + race condition entre forceResync (fire-and-forget) e o
      // sync handler. Resultado: prefetchAll era chamado mas {@code
      // queueDownload} pulava todos via {@code isInQueue} (queue não foi
      // limpa) ou {@code isCached} (status stale).
      //
      // Fix robusto: limpeza em ordem rigorosa + prefetch EXPLÍCITO após
      // o sync completar (não dependa do handler automático).
      console.log(`${LOG} 🧹 cache:clear cmd=${cmdId} INICIANDO`);

      // 1) Cancelar TODOS os downloads pendentes/em vôo. Sem isso, items
      //    ficam "isInQueue=true" e são pulados pelo próximo queueDownload.
      console.log(`${LOG}    └─ Cancelando downloads pendentes...`);
      mediaStore.cancelAllDownloads();

      // 2) Limpar cache físico (IndexedDB/Filesystem/CacheAPI conforme plataforma).
      console.log(`${LOG}    └─ Limpando cache físico (${itemCount} items)...`);
      await cache.clear();

      // 3) Metadata espelhada em localStorage fica órfã sem limpeza explícita.
      try {
        localStorage.removeItem('efflux_media_metadata');
      } catch {
        /* ignore quota errors */
      }

      // 4) Limpar cacheStatus Map MobX. Sem isso, isCached() retorna true
      //    e prefetchAll pula tudo via "All items already cached".
      console.log(`${LOG}    └─ Limpando cacheStatus in-memory...`);
      mediaStore.cacheStatus.clear();

      // 4b) Frames de transição (último frame de cada vídeo, guardado pra
      //     transição seamless). Ficam órfãos aqui: apontam pra mídia que o
      //     passo 2 acabou de apagar. Cada entrada retém um HTMLImageElement,
      //     que prende o bitmap DECODIFICADO (~8MB a 1080p) — não o JPEG.
      //     Até 2026-09-18 este `clear()` não tinha NENHUM chamador em
      //     produção (o comentário do módulo afirmava o contrário), então o
      //     Map nunca era esvaziado. Hoje ele tem teto/LRU, mas limpar junto
      //     com o cache de mídia é o correto: o conteúdo referenciado não
      //     existe mais.
      console.log(
        `${LOG}    └─ Limpando frames de transição (lastFrameCache)...`,
      );
      lastFrameCache.clear();

      // 5) Forçar resync E AGUARDAR (não fire-and-forget). Importante pra
      //    o prefetch explícito abaixo ter acesso aos playlistItems frescos.
      console.log(`${LOG}    └─ Forçando resync (await)...`);
      try {
        await syncStore.forceResync();
      } catch (err: unknown) {
        console.warn(`${LOG} cache:clear forceResync falhou:`, err);
      }

      // 6) Disparar prefetchAll EXPLICITAMENTE com a playlist atual.
      //    syncCacheInBackground já é chamado pelo handler do sync mas com
      //    estado possivelmente stale. Esta chamada explícita garante que
      //    a fila é repopulada com a playlist mais atual do schedulerStore.
      // Coleta playlist atual do scheduler (todas as campanhas ativas, já
      // ordenadas por priority + filtradas por horário). Esse é o universo
      // que o player precisa ter localmente pra rodar offline.
      const playlistItems = schedulerStore.getActivePlaylist();
      if (playlistItems.length > 0) {
        console.log(
          `${LOG}    └─ Disparando prefetchAll explícito (${playlistItems.length} items)...`,
        );
        // fire-and-forget — downloads rodam em background
        void mediaStore
          .prefetchAll(playlistItems)
          .catch((err: unknown) =>
            console.warn(`${LOG} prefetchAll explícito falhou:`, err),
          );
      } else {
        console.warn(
          `${LOG}    └─ ⚠️ Sem playlistItems pra prefetch após sync. Re-download dependerá do próximo sync.`,
        );
      }

      console.log(
        `${LOG} 🧹 cache:clear cmd=${cmdId} CONCLUÍDO — items=${itemCount} bytes=${before.usage}`,
      );
      return {
        status: 'success',
        result: {
          items_cleared: itemCount,
          bytes_freed: before.usage,
          resync_triggered: true,
          prefetch_queued: playlistItems.length,
        },
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { status: 'failed', error: msg };
    }
  }

  /**
   * sync:force — dispara {@code syncStore.forceResync()} que limpa o hash
   * local e baixa a media-library inteira do server, ignorando otimizações
   * de delta sync.
   *
   * <p>Cenário de uso: admin trocou campanha no backoffice e o player ainda
   * não pegou (SSE perdeu, polling não rodou ainda). Operador força sync
   * imediato sem esperar o ciclo normal.</p>
   *
   * <p>Diferente de {@code sync()} normal: {@code forceResync()} ignora
   * cache de hash da library e sempre processa a resposta como mudança.</p>
   */
  private async handleSyncForce(cmdId: string): Promise<HandlerResult> {
    try {
      const startMs = Date.now();
      await syncStore.forceResync();
      const elapsedMs = Date.now() - startMs;
      console.log(`${LOG} 🔄 sync:force cmd=${cmdId} elapsed_ms=${elapsedMs}`);
      return {
        status: 'success',
        result: { elapsed_ms: elapsedMs },
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { status: 'failed', error: msg };
    }
  }

  /**
   * logs:tail — retorna últimas N entradas do ring buffer in-memory
   * mantido pelo {@link LogRingBuffer}. Buffer capturou desde o boot do
   * app (inicializado em {@code src/index.tsx}).
   *
   * <p>Params aceitos: {@code lines} (number, default 100, cap = capacity
   * do buffer = 500). Valores fora do range são clamped silenciosamente.</p>
   *
   * <p>Cenário: operador remoto recebe report "vídeo travando às 14h35".
   * Dispara {@code logs:tail} com {@code lines=200} pelo admin → recebe
   * snapshot das últimas N entries serializadas no resultado do comando,
   * visíveis na seção "Eventos ao vivo / Histórico" da Visualização
   * Interativa. Não exige feature flag de remote-logging nem deslocamento.</p>
   *
   * <p>Filtragem por level (e.g., só errors) ou por janela temporal fica
   * pra evolução futura — hoje retorna tudo cronologicamente.</p>
   */
  private async handleLogsTail(
    cmdId: string,
    params: Record<string, unknown>,
  ): Promise<HandlerResult> {
    try {
      const requested = typeof params.lines === 'number' ? params.lines : 100;
      const logs = logRingBuffer.tail(requested);

      // v1.0.44 — incluir crashes do localStorage (sobrevivem ao crash do app).
      //
      // Caso real (28/05): TV TIZEN fechou sozinha sem mensagem visível.
      // dlog bloqueado pelo secure_protocol do Tizen Consumer 9.0; WebInspector
      // exige rebuild com flag debug. Solução: o crashCapture já instala
      // window.onerror + unhandledrejection no boot e persiste em localStorage.
      // No próximo boot, getRecentCrashes() retorna o que foi salvo antes do
      // crash anterior. Esse comando agora também devolve esses crashes para o
      // admin via Visualização Interativa → o operador vê timestamp + mensagem
      // + stack do crash mais recente sem precisar estar na frente da TV.
      const crashes = getRecentCrashes();

      console.log(
        `${LOG} 📜 logs:tail cmd=${cmdId} returned=${logs.length} buffered=${logRingBuffer.size()} crashes=${crashes.length}`,
      );
      return {
        status: 'success',
        result: {
          logs,
          returned: logs.length,
          total_buffered: logRingBuffer.size(),
          capacity: logRingBuffer.getCapacity(),
          crashes,
          crashes_count: crashes.length,
        },
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { status: 'failed', error: msg };
    }
  }

  /**
   * kiosk:clear-device-owner — Recovery do Kiosk Onda A (HARD-19/HARD-9).
   *
   * <p>Remove TODAS as policies de Device Owner aplicadas: Lock Task features,
   * uninstall block, user restrictions (DISALLOW_INSTALL_UNKNOWN_SOURCES,
   * DISALLOW_USB_FILE_TRANSFER, DISALLOW_FACTORY_RESET). Wi-Fi config is not
   * blocked anymore so field technicians can connect the store network.
   * Após executar, o app perde o status de Device Owner e volta a ser
   * "removível" pelo Android.</p>
   *
   * <p>Operação <b>irreversível</b> remotamente — reprovisionar exige acesso
   * físico ao tablet via ADB ({@code dpm set-device-owner}). Por isso o admin
   * UI implementa modal de confirmação dupla antes de disparar.</p>
   *
   * <p>Cenário: tablet provisionado como DO ficou "preso" — kiosk aplicado,
   * mas o operador precisa atualizar manualmente ou trocar o app que não é o
   * efflux. Em vez de deslocar técnico até a loja, recovery libera o tablet.</p>
   *
   * <p>Retorna {@code unsupported} em:</p>
   * <ul>
   *   <li>Plataformas não-Android (browser, TV) — plugin nativo no-op</li>
   *   <li>Android sem plugin KioskMode (build sem o plugin custom)</li>
   *   <li>Android com plugin mas backend != Device Owner (Stock Android)</li>
   * </ul>
   */
  private async handleKioskClearDeviceOwner(
    cmdId: string,
  ): Promise<HandlerResult> {
    const result = await tryClearDeviceOwner();
    if (result === null) {
      return {
        status: 'unsupported',
        error:
          'kiosk:clear-device-owner — plugin KioskMode indisponível (build sem plugin nativo ou plataforma não-Android)',
      };
    }
    if (!result.supported) {
      return {
        status: 'unsupported',
        error:
          result.reason ??
          `kiosk:clear-device-owner não suportado pelo backend ${result.backend}`,
      };
    }
    console.log(
      `${LOG} 🔓 kiosk:clear-device-owner cmd=${cmdId} backend=${result.backend} status=${result.status ?? 'cleared'}`,
    );
    return {
      status: 'success',
      result: {
        backend: result.backend,
        status: result.status ?? 'device owner cleared',
      },
    };
  }

  /**
   * kiosk:get-state — admin pede o estado real do kiosk no tablet.
   *
   * <p>Cenário (2026-05-31, Renato): admin abre a tela de detalhe do player
   * e quer saber se o tablet **realmente** está locked, não só o que ele
   * solicitou via `kioskModeEnabled`. Antes não tinha visibilidade — se o
   * operador desbloqueou local com 7-clicks, o admin não sabia.</p>
   *
   * <p>Retorna 3 estados independentes pra UI distinguir:
   * <ul>
   *   <li>{@code serverEnabled}: admin pediu kiosk ON?</li>
   *   <li>{@code manuallyUnlocked}: operador desbloqueou local nessa sessão?</li>
   *   <li>{@code isLocked}: estado efetivo (combina os dois + plataforma)</li>
   * </ul>
   * Mais o {@code backend} nativo (Device Owner / Stock / N/A) pro admin
   * saber se o tablet tem privilégio pra realmente trancar.</p>
   *
   * <p>Read-only — não muda nada no player. Idempotente.</p>
   */
  private async handleKioskGetState(cmdId: string): Promise<HandlerResult> {
    const backend = await getNativeKioskBackend();
    const platform =
      (window as unknown as { __effluxPlatform?: { platform: string } })
        .__effluxPlatform?.platform || 'browser';

    const state = {
      server_enabled: kioskStore.isServerEnabled,
      // Inferido: se serverEnabled=true E isLocked=false E não é browser,
      // operador desbloqueou local (manuallyUnlocked). Não expomos o flag
      // privado diretamente — derivamos pra reduzir surface.
      manually_unlocked:
        kioskStore.isServerEnabled &&
        !kioskStore.isLocked &&
        platform !== 'browser',
      is_locked: kioskStore.isLocked,
      platform,
      backend: backend
        ? {
            name: backend.name,
            available: backend.available,
            can_silent_install: backend.canSilentInstall,
          }
        : null,
    };
    console.log(
      `${LOG} 🔍 kiosk:get-state cmd=${cmdId}`,
      JSON.stringify(state),
    );
    return { status: 'success', result: state };
  }

  /**
   * v1.0.110 (Sprint K-Pre-Cert Onda 1.4) — RemoteOps Tizen B2B SSSP.
   * Todos os handlers abaixo degradam graciosamente em Consumer / sem
   * Partner Cert: retornam `{status: 'unsupported', error: reason}`.
   *
   * Quando Partner Cert chegar + privileges B2B destravam, basta re-build
   * + re-deploy WGT — handlers começam a retornar success automaticamente.
   */

  /** kiosk:set-usb-lock {locked: boolean} — SSSP only. */
  private async handleKioskSetUSBLock(
    _cmdId: string,
    params: Record<string, unknown>,
  ): Promise<HandlerResult> {
    const locked = Boolean(params.locked ?? params.lock ?? true);
    const result = await setTizenUSBLock(locked);
    if (!result.supported) {
      return { status: 'unsupported', error: result.reason };
    }
    return { status: 'success', result: { locked } };
  }

  /** kiosk:set-ir-remote-lock {locked: boolean} — SSSP only, anti-brick. */
  private async handleKioskSetIRRemoteLock(
    _cmdId: string,
    params: Record<string, unknown>,
  ): Promise<HandlerResult> {
    const locked = Boolean(params.locked ?? params.lock ?? true);
    const result = await setTizenIRRemoteLock(locked);
    if (!result.supported) {
      return { status: 'unsupported', error: result.reason };
    }
    return { status: 'success', result: { locked } };
  }

  /** kiosk:schedule-power {on_time: "HH:MM", off_time: "HH:MM"} — SSSP only. */
  private async handleKioskSchedulePower(
    _cmdId: string,
    params: Record<string, unknown>,
  ): Promise<HandlerResult> {
    const onTime = String(params.on_time ?? params.onTime ?? '');
    const offTime = String(params.off_time ?? params.offTime ?? '');
    const result = await setTizenScheduledPower(onTime, offTime);
    if (!result.supported) {
      return { status: 'unsupported', error: result.reason };
    }
    return {
      status: 'success',
      result: { on_time: onTime, off_time: offTime },
    };
  }

  /** kiosk:reboot-tv — SSSP only (b2bcontrol.rebootDevice). */
  private async handleKioskRebootTV(): Promise<HandlerResult> {
    const result = await rebootTizenDevice();
    if (!result.supported) {
      return { status: 'unsupported', error: result.reason };
    }
    return { status: 'success', result: { rebooting: true } };
  }

  /**
   * ota:install-tizen {wgt_url} — instala WGT silenciosamente.
   * SSSP only (privilege packagemanager.install). Em Consumer / sem cert
   * retorna unsupported — operador precisa reinstalar via Tizen Studio.
   */
  private async handleOtaInstallTizen(
    _cmdId: string,
    params: Record<string, unknown>,
  ): Promise<HandlerResult> {
    const wgtUrl = String(params.wgt_url ?? params.wgtUrl ?? '');
    if (!wgtUrl) {
      return {
        status: 'failed',
        error: 'parâmetro wgt_url obrigatório (URL HTTPS do .wgt)',
      };
    }
    const result = await installTizenPackage(wgtUrl);
    if (!result.supported) {
      return { status: 'unsupported', error: result.reason };
    }
    return { status: 'success', result: { wgt_url: wgtUrl, started: true } };
  }

  /** kiosk:get-capabilities — admin probe quais APIs B2B estão disponíveis. */
  private async handleKioskGetCapabilities(): Promise<HandlerResult> {
    const caps = await getTizenKioskCapabilities();
    return { status: 'success', result: caps };
  }

  /**
   * ota:check — dispara {@code otaStore.checkForUpdate()}, equivalente ao
   * que o polling natural do OtaService roda no intervalo configurado.
   *
   * <p>Cenário: admin acabou de publicar release nova + criar rollout, mas
   * polling do player só roda daqui a X min. Operador clica "Forçar update
   * agora" no admin → server dispara este cmd via SSE pra todos os devices
   * targeted → cada player chama {@code GET /api/ota/check} imediatamente.</p>
   *
   * <p>Idempotência: chamadas duplicadas são seguras. {@code otaStore.checkForUpdate}
   * tem proteção própria contra concorrência (não detém lock, mas sobrescreve
   * lastCheckTime + status na última resposta). Player decide se já tem check
   * em andamento — backend não deduplica.</p>
   *
   * <p>Result: retorna {@code updateAvailable} + {@code version} se houver
   * release pendente, ou {@code updateAvailable=false} caso contrário. Erros
   * são tratados internamente pelo store (status=failed, error message) e
   * traduzidos aqui pro contrato do remote-ops cmdResult.</p>
   */
  private async handleOtaCheck(
    cmdId: string,
    params: Record<string, unknown>,
  ): Promise<HandlerResult> {
    // v1.0.46 — logging defensivo da chain inteira do ota:check.
    //
    // Bug investigado (28/05): Tab A7 retornava update_available=false mas
    // server NUNCA recebia chamada em /api/ota/check. Sintoma: chain do
    // OtaService chama isOnline()→false OU axios.get falha silenciosa no
    // catch (linhas 168-175 e 231-239 do OtaService). Sem os logs abaixo
    // não dá pra discriminar qual branch dispara.
    console.log(
      `${LOG} 📦 ota:check IN cmd=${cmdId} ` +
        `isInit=${otaStore.isInitialized} ` +
        `navOnline=${typeof navigator !== 'undefined' ? navigator.onLine : 'n/a'} ` +
        `currentVersion=${otaStore.currentVersion}`,
    );

    // Embedded: o OTA do bundle é NATIVO (o AAR baixa e troca). O `otaStore` daqui
    // cuida do APK da frota e nem é inicializado, então antes desta ponte o comando
    // morria com `unsupported` e o bundle só atualizava no ciclo de 1 h do AAR.
    // Repassa ao host — e só reporta sucesso se a ponte existir de fato (AAR < 1.0.444
    // não expõe `requestOtaCheck`; responder sucesso ali seria falso positivo no admin).
    if (detectPlatform() === 'embedded') {
      const ponte = window.EffluxHost;
      if (typeof ponte?.requestOtaCheck !== 'function') {
        console.warn(
          `${LOG} ⚠️ ota:check unsupported — AAR sem requestOtaCheck (anterior a 1.0.444)`,
        );
        return {
          status: 'unsupported',
          error:
            'AAR desta build não expõe requestOtaCheck (exige AAR 1.0.444+) — atualize o .aar para forçar OTA do bundle embedded',
        };
      }
      ponte.requestOtaCheck();
      console.log(
        `${LOG} 📦 ota:check encaminhado ao host nativo cmd=${cmdId}`,
      );
      return {
        status: 'success',
        result: {
          dispatched_to_host: true,
          current_version: detectEngineVersion(),
          rollout_id:
            typeof params.rollout_id === 'string' ? params.rollout_id : null,
          force_all: params.force_all === true,
        },
      };
    }

    if (!otaStore.isInitialized) {
      console.warn(
        `${LOG} ⚠️ ota:check unsupported — otaStore not initialized`,
      );
      return {
        status: 'unsupported',
        error:
          'OtaStore não inicializado (OTA service não configurado nesta build)',
      };
    }

    try {
      const beforeUpdate = otaStore.updateAvailable;
      const beforeRelease = otaStore.currentRelease?.version ?? null;
      await otaStore.checkForUpdate();
      const updateAvailable = otaStore.updateAvailable;
      const version = otaStore.currentRelease?.version ?? null;
      const error = otaStore.error;

      console.log(
        `${LOG} 📦 ota:check OUT cmd=${cmdId} ` +
          `updateAvailable=${beforeUpdate}→${updateAvailable} ` +
          `version=${beforeRelease}→${version} ` +
          `error=${error ?? 'none'}`,
      );

      if (error) {
        return { status: 'failed', error };
      }

      return {
        status: 'success',
        result: {
          update_available: updateAvailable,
          version,
          current_version: otaStore.currentVersion,
          rollout_id:
            typeof params.rollout_id === 'string' ? params.rollout_id : null,
          force_all: params.force_all === true,
          last_check: otaStore.lastCheckTime,
        },
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error(`${LOG} ❌ ota:check threw cmd=${cmdId}: ${msg}`);
      return { status: 'failed', error: msg };
    }
  }

  /** Factory pra handlers que apenas retornam unsupported com mensagem custom. */
  /**
   * WIFI_LIST_PLAN — wifi:list. Lista a rede conectada + redes proximas (scan)
   * via plugin nativo (Android Device Owner). Em plataformas sem o plugin
   * (browser, TV, Android sem o plugin) retorna unsupported.
   *
   * <p>Result: {@code { wifiEnabled, permissionGranted, connected, networks,
   * networkCount }} — vide WifiListResult em kioskMode.ts.</p>
   */
  private async handleWifiList(cmdId: string): Promise<HandlerResult> {
    const res = await listWifiNetworks();
    if (res === null) {
      return {
        status: 'unsupported',
        error:
          'wifi:list indisponivel (plugin KioskMode ausente ou plataforma nao-Android)',
      };
    }
    console.log(
      `${LOG} 📶 wifi:list cmd=${cmdId} enabled=${res.wifiEnabled} ` +
        `perm=${res.permissionGranted} count=${res.networkCount} ` +
        `connected=${res.connected?.ssid ?? 'none'}`,
    );
    return { status: 'success', result: res };
  }

  /**
   * WIFI_CONNECT_PLAN — wifi:connect. Conecta a uma rede escolhida no admin
   * (params {@code { ssid, password, secured }}) via plugin nativo (Android
   * Device Owner). Em plataformas sem o plugin retorna unsupported.
   *
   * <p>⚠️ Trocar de rede DERRUBA a conexão atual — por isso retornamos o
   * resultado imediato; o admin trata como fire-and-forget e o device reassocia
   * sozinho. Em caso de SSID ausente, retorna error.</p>
   */
  private async handleWifiConnect(
    cmdId: string,
    params: Record<string, unknown>,
  ): Promise<HandlerResult> {
    const ssid = typeof params.ssid === 'string' ? params.ssid : '';
    if (!ssid) {
      return { status: 'failed', error: 'wifi:connect requer ssid' };
    }
    const password =
      typeof params.password === 'string' ? params.password : undefined;
    const secured =
      typeof params.secured === 'boolean' ? params.secured : undefined;

    const res = await connectWifiNetwork({ ssid, password, secured });
    if (res === null) {
      return {
        status: 'unsupported',
        error:
          'wifi:connect indisponivel (plugin KioskMode ausente ou plataforma nao-Android)',
      };
    }
    console.log(
      `${LOG} 📶 wifi:connect cmd=${cmdId} ssid="${res.ssid}" ` +
        `netId=${res.netId} enabled=${res.enabled} — conexao vai cair e voltar`,
    );
    return { status: 'success', result: res };
  }

  private makeStub(reason: string): Handler {
    return async () => ({ status: 'unsupported', error: reason });
  }
}

// Singleton
const remoteOpsService = new RemoteOpsService();
export default remoteOpsService;

// Class export pra testes (instanciação isolada).
export { RemoteOpsService };
